import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import dayjs from "dayjs";
import { GlobalContext } from "../../../context/GlobalContext";
import axios from "../../../api/axios";
import helpers from "../../../utils/helpers";
import DeclarationModal from "../../questionscomponent/subcomponent/DeclarationModal";
import AppSnackbar from "../../shared/AppSnackbar";
import { CustomAddressAutocomplete } from "../../shared/CustomAddressAutoComplete";
import Birthday from "../../questionscomponent/subcomponent/Birthday";
import SecondOwnerBirthday from "../../questionscomponent/subcomponent/SecondOwnerBirthday";
import CoApplicantBirthday from "../../questionscomponent/subcomponent/personal/CoApplicantBirthday";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_REGEX = /^\(\d{3}\)-\d{3}-\d{4}$/;
const NAME_REGEX = /^.{1,}$/;
const SALES_REP_NAME_REGEX = /^.{3,}$/;
const ENTITY_TYPE_OPTIONS = [
  { value: "Corporation", label: "Corporation" },
  { value: "LLC", label: "LLC" },
  { value: "Sole Proprietorship", label: "Sole prop" },
  { value: "Other", label: "Other" },
];
const MARITAL_STATUS_OPTIONS = ["Single", "Married", "Separated"];
const CITIZENSHIP_OPTIONS = ["US Citizen", "Permanent Resident", "Resident Alien", "Other"];
const EMPLOYMENT_STATUS_OPTIONS = [
  "Retired or Disabled",
  "Retired or Disabled and also currently employed",
  "Employed",
];
const DISPOSITION_OPTIONS = ["Selling", "Trading in", "Keeping"];
const SUFFIX_OPTIONS = ["Jr.", "Sr.", "II", "III", "IV", "V"];
const HOUSING_STATUS_OPTIONS = ["Own", "Rent", "With Relatives", "With Friends"];
const ADDITIONAL_INCOME_SOURCE_OPTIONS = [
  "Social Security",
  "VA Disability",
  "Pension",
  "Retirement",
  "Rental Properties",
];
// Maps this card's autosave POST body keys back to the local blockErrors[key]
// field names that already have a render slot — lets a backend validation
// rejection surface under the specific field instead of only as the generic
// "Something went wrong saving your changes" snackbar. Shared across every
// card's save() since the backend key names don't collide between cards.
const SERVER_FIELD_ERROR_MAP = {
  ssn_number: "ssn",
  citizenship: "citizenship",
  personal_employment_status: "employmentStatus",
  personal_gross_monthly_income: "grossMonthlyIncome",
  personal_co_applicant_first_name: "coApplicantFirstName",
  personal_co_applicant_last_name: "coApplicantLastName",
  personal_co_applicant_email: "coApplicantEmail",
  personal_co_applicant_phone_no: "coApplicantPhone",
  personal_co_applicant_date_of_birth: "coApplicantDob",
  personal_co_applicant_employment_status: "coApplicantEmploymentStatus",
};

function formatPhoneNumber(digitsOnly) {
  if (!digitsOnly) return "";
  if (digitsOnly.length <= 3) return `(${digitsOnly}`;
  if (digitsOnly.length <= 6) return `(${digitsOnly.slice(0, 3)})-${digitsOnly.slice(3)}`;
  return `(${digitsOnly.slice(0, 3)})-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
}

function formatDashed(sanitizedValue, dashIndexes) {
  let formatted = "";
  for (let i = 0; i < sanitizedValue.length; i++) {
    formatted += dashIndexes.includes(i) ? `-${sanitizedValue[i]}` : sanitizedValue[i];
  }
  return formatted;
}

// Read-only display string for an SSN in the Review step: the full value,
// dashed as XXX-XX-XXXX. Only affects display — edit mode/autosave still
// round-trip the sanitized value unchanged.
function formatSSN(sanitizedOrDashedValue) {
  const sanitized = (sanitizedOrDashedValue || "").replace(/-/g, "");
  if (!sanitized) return null;
  return formatDashed(sanitized, [3, 5]);
}

function isDobValid(dob) {
  if (!dob) return false;
  const inputDate = new Date(dob);
  const currentDate = new Date();
  return inputDate <= currentDate;
}

const isWorking = (status) =>
  status === "Employed" || status === "Retired or Disabled and also currently employed";
const isRetiredIsh = (status) =>
  status === "Retired or Disabled" || status === "Retired or Disabled and also currently employed";

function makeEmptyOwnerRow(order) {
  return {
    order,
    email: "",
    phone: "",
    firstName: "",
    lastName: "",
    dob: null,
    ssn: "",
    ownershipPercentage: "",
    homeAddress: "",
    homeAddressComponents: [],
    homeAddressError: "",
    unitNumber: "",
  };
}

// Shared by every dollar-amount field added to this step's edit forms
// (retirement/other income, housing payment, annual revenue) — keeps the
// display value comma-formatted ("2,134") as the borrower types, same
// pattern as StepEquipment.js's/StepEmployment.js's own price fields.
function handleCurrencyChange(setter) {
  return (e) => {
    const cleaned = e.target.value.replace(/,/g, "").replace(/[^0-9]/g, "");
    const parsed = parseInt(cleaned);
    setter(isNaN(parsed) ? "" : parsed.toLocaleString());
  };
}

/**
 * PD-6 Step 5 "Review & Sign".
 *
 * Repurposes Confirmation.js's existing summary rendering (grouped into
 * blocks) and SignatureComponent.js's existing signature capture, fit into
 * the new step shell. The signature capture and the single
 * `submit-application` POST call are unchanged in behavior.
 *
 * Each card's "Edit" button used to jump the borrower back to that step
 * (goToStep) and rely on stepping forward again to return here. It's now an
 * in-place edit toggle instead: clicking it swaps the card's read-only
 * fields for the same inputs (and the same validation) as their origin
 * step, scoped to exactly the fields this card already shows — not that
 * step's full field set. Clicking it again (now labeled "Done") validates
 * and, if valid, dispatches into global state and fires the same
 * `partial-application` autosave POST(s) that step's own Continue uses for
 * those fields, then returns the card to read-only. Only the fields already
 * visible on this page are editable here; anything else (annual revenue,
 * years in business, suffix, tax ID, years/months employed, and so on)
 * still lives on its own step.
 *
 * This is the last step — the application now ends at signature, so a
 * successful submit calls `onSubmitted` directly (the Offers step that used
 * to follow this one has been removed; see steps.config.js).
 */
const StepReview = ({ goBack, onSubmitted, isFirstStep, footNote }) => {
  const { state, dispatch } = useContext(GlobalContext);
  const [showLoader, setShowLoader] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [open, setOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [authorizeAgreed, setAuthorizeAgreed] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [name, setName] = useState(state?.questions?.imageName ?? "");
  const [, setImageUrl] = useState(null);
  const canvasRef = useRef(null);

  // Stable reference — see AppSnackbar's own doc comment for why an inline
  // arrow function here would keep resetting the auto-hide timer. Also
  // clears errMsg, not just `open` — the toast is only mounted at all via
  // `{errMsg && <AppSnackbar .../>}` below, so leaving errMsg set kept it
  // mounted (at open=false) instead of tearing down cleanly, and left a
  // stale message ready to flash back on screen if `open` were ever set
  // true again before a new error set a fresh one.
  const handleClose = useCallback(() => {
    setOpen(false);
    setErrMsg("");
  }, []);

  // Mirrors StepShell.js's own derivation exactly: `state.isPersonalFlow` is
  // just the server-side permission flag ("this company/link may run the
  // personal path"), not the borrower's actual choice — that's
  // `questions.equipmentUse`, captured by StepYou.js's "How will you use
  // the equipment?" question (only asked when the permission flag is set).
  const isBusiness = !(
    Boolean(state?.isPersonalFlow) && state?.questions?.equipmentUse === "Personal"
  );

  const endpoint = `lender/partial-application/${state?.informationSlug}/?uuid=${state?.applicationUUID}`;

  // --- In-place card editing ------------------------------------------
  const [editing, setEditing] = useState({
    you: false,
    businessResidence: false,
    ownershipEmployment: false,
    equipment: false,
  });
  const [blockAttempted, setBlockAttempted] = useState({
    you: false,
    businessResidence: false,
    ownershipEmployment: false,
    equipment: false,
  });
  const [blockErrors, setBlockErrors] = useState({
    you: {},
    businessResidence: {},
    ownershipEmployment: {},
    equipment: {},
  });
  const [savingBlock, setSavingBlock] = useState(null);

  // "You" card drafts (date of birth is edited via <Birthday /> straight
  // against global state, same as StepYou.js does — no local draft needed).
  const [youEmail, setYouEmail] = useState(state?.questions?.email ?? "");
  const [youPhone, setYouPhone] = useState(() => {
    const stored = state?.questions?.phone ?? "";
    return formatPhoneNumber(stored.replace("+1", "").replace(/[^0-9]/g, ""));
  });
  const [youFirstName, setYouFirstName] = useState(state?.questions?.firstname ?? "");
  const [youLastName, setYouLastName] = useState(state?.questions?.lastname ?? "");
  const [youPurpleWaveCustomerNumber, setYouPurpleWaveCustomerNumber] = useState(
    state?.questions?.purpleWaveCustomerNumber ?? ""
  );
  // Matches StepYou.js's own gating — only Purple Wave listings show/edit
  // this field.
  const isPurpleWaveListing = Boolean(state?.isPurpleWaveAccount);
  // StepYou.js extras — equipment use + sales rep.
  const [youEquipmentUse, setYouEquipmentUse] = useState(
    state?.questions?.equipmentUse === "Personal" || state?.questions?.equipmentUse === "Business"
      ? state.questions.equipmentUse
      : ""
  );
  const [youHasSalesRep, setYouHasSalesRep] = useState(Boolean(state?.questions?.hasSalesRep));
  const [youSalesRepFirstName, setYouSalesRepFirstName] = useState(
    state?.questions?.salesRepFirstName ?? ""
  );
  const [youSalesRepLastName, setYouSalesRepLastName] = useState(
    state?.questions?.salesRepLastName ?? ""
  );
  const isPersonalFlowEnabled = Boolean(state?.isPersonalFlow);
  const isSalesRepFlow = Boolean(state?.isSalesRepFlow);
  const handleYouSalesRepOptionChange = (value) => {
    setYouHasSalesRep(value);
    if (!value) {
      setYouSalesRepFirstName("");
      setYouSalesRepLastName("");
    }
  };

  // "Business" card drafts.
  const [bizCompanyName, setBizCompanyName] = useState(
    state?.questions?.business?.companyName ?? ""
  );
  const [bizEntityType, setBizEntityType] = useState(state?.questions?.business?.entityType ?? "");
  const [bizCompanyAddress, setBizCompanyAddress] = useState(
    state?.questions?.business?.companyAddress ?? ""
  );
  const [bizCompanyAddressComponents, setBizCompanyAddressComponents] = useState(
    JSON.parse(localStorage.getItem("companyComponentsMazo")) ?? []
  );
  const [bizCompanyAddressError, setBizCompanyAddressError] = useState("");
  const [bizCompanyUnitNumber, setBizCompanyUnitNumber] = useState(
    state?.questions?.business?.companyUnitNumber ?? ""
  );
  const [bizHomeAddress, setBizHomeAddress] = useState(state?.questions?.business?.homeAddress ?? "");
  const [bizHomeAddressComponents, setBizHomeAddressComponents] = useState(
    JSON.parse(localStorage.getItem("homeComponentsMazo")) ?? []
  );
  const [bizHomeAddressError, setBizHomeAddressError] = useState("");
  const [bizHomeUnitNumber, setBizHomeUnitNumber] = useState(
    state?.questions?.business?.homeUnitNumber ?? ""
  );
  // Restored ticked when the saved addresses already match (unit lines
  // too), same as StepBusiness.js / StepResidence.js.
  const [bizSameAsCompanyAddress, setBizSameAsCompanyAddress] = useState(
    () =>
      Boolean(state?.questions?.business?.companyAddress) &&
      state?.questions?.business?.homeAddress === state?.questions?.business?.companyAddress &&
      (state?.questions?.business?.homeUnitNumber ?? "") ===
        (state?.questions?.business?.companyUnitNumber ?? "")
  );

  // "Residence" card drafts.
  const [resResidentialAddress, setResResidentialAddress] = useState(
    state?.questions?.personal?.residentialAddress ?? ""
  );
  const [resResidentialAddressComponents, setResResidentialAddressComponents] = useState(
    JSON.parse(localStorage.getItem("residentialComponentsMazo")) ?? []
  );
  const [resResidentialAddressError, setResResidentialAddressError] = useState("");
  const [resResidentialUnitNumber, setResResidentialUnitNumber] = useState(
    state?.questions?.personal?.residentialUnitNumber ?? ""
  );
  const [resMailingAddress, setResMailingAddress] = useState(
    state?.questions?.personal?.mailingAddress ?? ""
  );
  const [resMailingAddressComponents, setResMailingAddressComponents] = useState(
    JSON.parse(localStorage.getItem("mailingComponentsMazo")) ?? []
  );
  const [resMailingAddressError, setResMailingAddressError] = useState("");
  const [resMailingUnitNumber, setResMailingUnitNumber] = useState(
    state?.questions?.personal?.mailingUnitNumber ?? ""
  );
  const [resSameAsResidential, setResSameAsResidential] = useState(
    () =>
      Boolean(state?.questions?.personal?.residentialAddress) &&
      state?.questions?.personal?.mailingAddress === state?.questions?.personal?.residentialAddress &&
      (state?.questions?.personal?.mailingUnitNumber ?? "") ===
        (state?.questions?.personal?.residentialUnitNumber ?? "")
  );
  const [resMaritalStatus, setResMaritalStatus] = useState(
    state?.questions?.personal?.maritalStatus ?? ""
  );
  // StepBusiness.js extras.
  const [bizAnnualRevenue, setBizAnnualRevenue] = useState(
    state?.questions?.business?.bussinessAnual
      ? parseInt(state?.questions?.business?.bussinessAnual)?.toLocaleString()
      : ""
  );
  const [bizLongBussiness, setBizLongBussiness] = useState(
    state?.questions?.business?.longBussiness ?? ""
  );
  const [bizBusinessDescription, setBizBusinessDescription] = useState(
    state?.questions?.business?.businessDescription ?? ""
  );
  // StepResidence.js extras.
  const [resSuffix, setResSuffix] = useState(state?.questions?.personal?.suffix ?? "");
  const [resPlanToRegisterVehicle, setResPlanToRegisterVehicle] = useState(
    state?.questions?.personal?.isPlanToRegisterVehicle ?? true
  );
  const [resLivingYears, setResLivingYears] = useState(state?.questions?.personal?.livingYears ?? "");
  const [resLivingMonths, setResLivingMonths] = useState(
    state?.questions?.personal?.livingMonths ?? ""
  );
  const [resLivingMonthlyPayment, setResLivingMonthlyPayment] = useState(
    state?.questions?.personal?.livingMonthlyPayment
      ? parseInt(state?.questions?.personal?.livingMonthlyPayment)?.toLocaleString()
      : ""
  );
  const [resLivingStatus, setResLivingStatus] = useState(
    state?.questions?.personal?.livingStatus ?? ""
  );

  // Selecting a suggestion fills in the address text immediately, but its
  // structured components (required for validation) only land once the
  // place-details lookup resolves — same race guard the origin steps use.
  const [pendingAddressLookups, setPendingAddressLookups] = useState(0);
  const handleAddressResolvingChange = (isResolving) => {
    setPendingAddressLookups((count) => count + (isResolving ? 1 : -1));
  };

  // "Ownership" card drafts.
  const [ownSsn, setOwnSsn] = useState(() => formatDashed(state?.questions?.ssn ?? "", [3, 5]));
  const [ownCitizenship, setOwnCitizenship] = useState(state?.questions?.citizenship ?? "");
  const [ownOwnershipPercentage, setOwnOwnershipPercentage] = useState(
    state?.questions?.business?.ownershipPercentage ?? 100
  );
  // StepOwnership.js extras — Tax ID + the full additional-owners list.
  const [ownTaxId, setOwnTaxId] = useState(() =>
    formatDashed(state?.questions?.business?.taxId ?? "", [2])
  );
  const [ownAdditionalOwners, setOwnAdditionalOwners] = useState(() => {
    const saved = state?.questions?.business?.additionalOwners;
    if (!Array.isArray(saved) || saved.length === 0) return [];
    return saved.map((owner) => ({
      order: owner.order,
      email: owner.email ?? "",
      phone: formatPhoneNumber((owner.phone ?? "").replace("+1", "").replace(/[^0-9]/g, "")),
      firstName: owner.firstName ?? "",
      lastName: owner.lastName ?? "",
      dob: owner.dob ? dayjs(owner.dob) : null,
      ssn: formatDashed((owner.ssn ?? "").replace(/-/g, ""), [3, 5]),
      ownershipPercentage: owner.ownershipPercentage ?? "",
      homeAddress: owner.homeAddress ?? "",
      homeAddressComponents: owner.homeAddressComponents ?? [],
      homeAddressError: "",
      unitNumber: owner.unitNumber ?? "",
    }));
  });
  const [ownOwnerRowErrors, setOwnOwnerRowErrors] = useState({});
  // PD-22: 80% threshold (was 100%) — see the matching comment in
  // StepOwnership.js's own hasAdditionalOwners.
  const ownHasAdditionalOwners = ownOwnershipPercentage !== "" && Number(ownOwnershipPercentage) < 80;
  const ownRemainingShare = ownOwnershipPercentage === "" ? "" : 100 - Number(ownOwnershipPercentage);
  const ownAllocatedShare = ownAdditionalOwners.reduce(
    (sum, owner) => sum + (Number(owner.ownershipPercentage) || 0),
    0
  );
  const ownTotalAssignedShare = ownHasAdditionalOwners
    ? Number(ownOwnershipPercentage) + ownAllocatedShare
    : 0;
  const ownTotalUnassignedShare = 100 - ownTotalAssignedShare;
  const updateOwnerRow = (order, patch) => {
    setOwnAdditionalOwners((rows) =>
      rows.map((row) => (row.order === order ? { ...row, ...patch } : row))
    );
  };
  const addOwnerRow = () => {
    setOwnAdditionalOwners((rows) => [
      ...rows,
      makeEmptyOwnerRow((rows[rows.length - 1]?.order ?? 0) + 1),
    ]);
  };
  const removeOwnerRow = (order) => {
    setOwnAdditionalOwners((rows) =>
      rows.filter((row) => row.order !== order).map((row, idx) => ({ ...row, order: idx + 1 }))
    );
  };
  const handleOwnerPhoneChange = (order, e) => {
    const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
    if (digitsOnly.length <= 10) updateOwnerRow(order, { phone: formatPhoneNumber(digitsOnly) });
  };
  const handleOwnerSSNChange = (order, e) => {
    const sanitized = e.target.value.replace(/[^A-Za-z0-9]/g, "");
    if (sanitized.length < 10) updateOwnerRow(order, { ssn: formatDashed(sanitized, [3, 5]) });
  };

  // "Employment" card drafts.
  const [empEmploymentStatus, setEmpEmploymentStatus] = useState(
    state?.questions?.personal?.employmentStatus ?? ""
  );
  const [empBusinessName, setEmpBusinessName] = useState(
    state?.questions?.personal?.businessName ?? ""
  );
  const [empGrossMonthlyIncome, setEmpGrossMonthlyIncome] = useState(
    state?.questions?.personal?.grossMonthlyIncome ?? ""
  );
  // StepEmployment.js extras — primary applicant.
  const [empBusinessPhone, setEmpBusinessPhone] = useState(() => {
    const stored = state?.questions?.personal?.businessPhone ?? "";
    return formatPhoneNumber(stored.replace("+1", "").replace(/[^0-9]/g, ""));
  });
  const [empOccupationTitle, setEmpOccupationTitle] = useState(
    state?.questions?.personal?.occupationTitle ?? ""
  );
  const [empEmployerAddress, setEmpEmployerAddress] = useState(
    state?.questions?.personal?.employerAddress ?? ""
  );
  const [empEmployerAddressComponents, setEmpEmployerAddressComponents] = useState(
    JSON.parse(localStorage.getItem("employerComponentsMazo")) ?? []
  );
  const [empEmployerAddressError, setEmpEmployerAddressError] = useState("");
  const [empEmployerUnitNumber, setEmpEmployerUnitNumber] = useState(
    state?.questions?.personal?.employerUnitNumber ?? ""
  );
  const [empRetirementIncome, setEmpRetirementIncome] = useState(
    state?.questions?.personal?.retirementIncome
      ? parseInt(state?.questions?.personal?.retirementIncome)?.toLocaleString()
      : ""
  );
  const [empRetirementYears, setEmpRetirementYears] = useState(
    state?.questions?.personal?.retirementYears ?? ""
  );
  const [empRetirementMonths, setEmpRetirementMonths] = useState(
    state?.questions?.personal?.retirementMonths ?? ""
  );
  const [empEmploymentYears, setEmpEmploymentYears] = useState(
    state?.questions?.personal?.employmentYears ?? ""
  );
  const [empEmploymentMonths, setEmpEmploymentMonths] = useState(
    state?.questions?.personal?.employmentMonths ?? ""
  );
  const [empAdditionalIncomeSource, setEmpAdditionalIncomeSource] = useState(
    state?.questions?.personal?.additionalIncomeSource ?? ""
  );
  const [empAdditionalMonthlyIncome, setEmpAdditionalMonthlyIncome] = useState(
    state?.questions?.personal?.additionalMonthlyIncome
      ? parseInt(state?.questions?.personal?.additionalMonthlyIncome)?.toLocaleString()
      : ""
  );

  // Co-applicant block (StepEmployment.js parity).
  const [coAppIsCoApplicant, setCoAppIsCoApplicant] = useState(
    Boolean(state?.questions?.personal?.isCoApplicant)
  );
  const [coAppFirstName, setCoAppFirstName] = useState(
    state?.questions?.personal?.coApplicantFirstName ?? ""
  );
  const [coAppLastName, setCoAppLastName] = useState(
    state?.questions?.personal?.coApplicantLastName ?? ""
  );
  const [coAppEmail, setCoAppEmail] = useState(state?.questions?.personal?.coApplicantEmail ?? "");
  const [coAppPhone, setCoAppPhone] = useState(() => {
    const stored = state?.questions?.personal?.coApplicantPhone ?? "";
    return formatPhoneNumber(stored.replace("+1", "").replace(/[^0-9]/g, ""));
  });
  const [coAppSsn, setCoAppSsn] = useState(() =>
    formatDashed((state?.questions?.personal?.coApplicantSsn ?? "").replace(/\D/g, ""), [3, 5])
  );
  const [coAppCitizenship, setCoAppCitizenship] = useState(
    state?.questions?.personal?.coApplicantCitizenship ?? ""
  );
  const [coAppSuffix, setCoAppSuffix] = useState(state?.questions?.personal?.coApplicantSuffix ?? "");
  const [coAppMaritalStatus, setCoAppMaritalStatus] = useState(
    state?.questions?.personal?.coApplicantMaritalStatus ?? ""
  );
  const [coAppResidentialAddress, setCoAppResidentialAddress] = useState(
    state?.questions?.personal?.coApplicantResidentialAddress ?? ""
  );
  const [coAppResidentialAddressComponents, setCoAppResidentialAddressComponents] = useState(
    JSON.parse(localStorage.getItem("coApplicantResidentialComponentsMazo")) ?? []
  );
  const [coAppResidentialAddressError, setCoAppResidentialAddressError] = useState("");
  const [coAppResidentialUnitNumber, setCoAppResidentialUnitNumber] = useState(
    state?.questions?.personal?.coApplicantResidentialUnitNumber ?? ""
  );
  const [coAppLivingYears, setCoAppLivingYears] = useState(
    state?.questions?.personal?.coApplicantLivingYears ?? ""
  );
  const [coAppLivingMonths, setCoAppLivingMonths] = useState(
    state?.questions?.personal?.coApplicantLivingMonths ?? ""
  );
  const [coAppLivingMonthlyPayment, setCoAppLivingMonthlyPayment] = useState(
    state?.questions?.personal?.coApplicantLivingMonthlyPayment
      ? parseInt(state?.questions?.personal?.coApplicantLivingMonthlyPayment)?.toLocaleString()
      : ""
  );
  const [coAppLivingStatus, setCoAppLivingStatus] = useState(
    state?.questions?.personal?.coApplicantLivingStatus ?? ""
  );
  const [coAppEmploymentStatus, setCoAppEmploymentStatus] = useState(
    state?.questions?.personal?.coApplicantEmploymentStatus ?? ""
  );
  const [coAppBusinessName, setCoAppBusinessName] = useState(
    state?.questions?.personal?.coApplicantBusinessName ?? ""
  );
  const [coAppGrossMonthlyIncome, setCoAppGrossMonthlyIncome] = useState(
    state?.questions?.personal?.coApplicantGrossMonthlyIncome
      ? parseInt(state?.questions?.personal?.coApplicantGrossMonthlyIncome)?.toLocaleString()
      : ""
  );
  const [coAppEmployerAddress, setCoAppEmployerAddress] = useState(
    state?.questions?.personal?.coApplicantEmployerAddress ?? ""
  );
  const [coAppEmployerAddressComponents, setCoAppEmployerAddressComponents] = useState([]);
  const [coAppEmployerAddressError, setCoAppEmployerAddressError] = useState("");
  const [coAppEmployerUnitNumber, setCoAppEmployerUnitNumber] = useState(
    state?.questions?.personal?.coApplicantEmployerUnitNumber ?? ""
  );
  const [coAppRetirementIncome, setCoAppRetirementIncome] = useState(
    state?.questions?.personal?.coApplicantretirementIncome
      ? parseInt(state?.questions?.personal?.coApplicantretirementIncome)?.toLocaleString()
      : ""
  );
  const [coAppRetirementYears, setCoAppRetirementYears] = useState(
    state?.questions?.personal?.coApplicantretirementYears ?? ""
  );
  const [coAppRetirementMonths, setCoAppRetirementMonths] = useState(
    state?.questions?.personal?.coApplicantretirementMonths ?? ""
  );
  const [coAppEmploymentYears, setCoAppEmploymentYears] = useState(
    state?.questions?.personal?.coApplicantEmploymentYears ?? ""
  );
  const [coAppEmploymentMonths, setCoAppEmploymentMonths] = useState(
    state?.questions?.personal?.coApplicantEmploymentMonths ?? ""
  );
  const [coAppAdditionalIncomeSource, setCoAppAdditionalIncomeSource] = useState(
    state?.questions?.personal?.coApplicantAdditionalIncomeSource ?? ""
  );
  const [coAppAdditionalMonthlyIncome, setCoAppAdditionalMonthlyIncome] = useState(
    state?.questions?.personal?.coApplicantAdditionalMonthlyIncome
      ? parseInt(state?.questions?.personal?.coApplicantAdditionalMonthlyIncome)?.toLocaleString()
      : ""
  );

  // "Equipment" card drafts.
  // Same condition as the Equipment step's ICN field: Purple Wave account
  // and no ICN in the link — i.e. the borrower typed it, so they can check it.
  const showReviewPurpleWaveIcn =
    Boolean(state?.isPurpleWaveAccount) && !state?.questions?.purpleWaveIcnFromLink;
  const [eqPurpleWaveIcn, setEqPurpleWaveIcn] = useState(state?.questions?.purpleWaveIcn ?? "");
  const [eqYear, setEqYear] = useState(state?.questions?.personal?.financingYear ?? "");
  const [eqMake, setEqMake] = useState(state?.questions?.personal?.financingMake ?? "");
  const [eqModel, setEqModel] = useState(state?.questions?.personal?.financingModel ?? "");
  const [eqCost, setEqCost] = useState(
    state?.questions?.equipmentCost
      ? parseInt(state?.questions?.equipmentCost)?.toLocaleString()
      : ""
  );
  const [eqDepositAmount, setEqDepositAmount] = useState(
    state?.questions?.personal?.downPayment
      ? parseInt(state?.questions?.personal?.downPayment)?.toLocaleString()
      : ""
  );
  const [eqPurchaseType, setEqPurchaseType] = useState(state?.questions?.business?.purchaseType ?? "");
  const [eqReplacingDescription, setEqReplacingDescription] = useState(
    state?.questions?.business?.replacingDescription ?? ""
  );
  const [eqReplacingDisposition, setEqReplacingDisposition] = useState(
    state?.questions?.business?.replacingDisposition ?? ""
  );

  const handleYouPhoneChange = (e) => {
    const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
    if (digitsOnly.length <= 10) setYouPhone(formatPhoneNumber(digitsOnly));
  };

  // Backend field (business_duration) is a plain IntegerField, 1-100 — same
  // clamp StepBusiness.js's own field uses.
  const handleBizLongBussinessChange = (e) => {
    const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
    if (digitsOnly === "") return setBizLongBussiness("");
    setBizLongBussiness(Math.min(parseInt(digitsOnly, 10), 100));
  };

  const handleBizSameAsCompanyAddressChange = (e) => {
    const checked = e.target.checked;
    setBizSameAsCompanyAddress(checked);
    if (checked) {
      setBizHomeAddress(bizCompanyAddress);
      setBizHomeAddressComponents(bizCompanyAddressComponents);
      setBizHomeAddressError("");
      setBizHomeUnitNumber(bizCompanyUnitNumber);
      // Persisted like a real selection, so a remount can't leave the
      // locked field without the components its validation needs.
      localStorage.setItem("homeComponentsMazo", JSON.stringify(bizCompanyAddressComponents));
    } else {
      setBizHomeAddress("");
      setBizHomeAddressComponents([]);
      setBizHomeUnitNumber("");
      localStorage.removeItem("homeComponentsMazo");
    }
  };

  const handleResSameAsResidentialChange = (e) => {
    const checked = e.target.checked;
    setResSameAsResidential(checked);
    if (checked) {
      setResMailingAddress(resResidentialAddress);
      setResMailingAddressComponents(resResidentialAddressComponents);
      setResMailingAddressError("");
      setResMailingUnitNumber(resResidentialUnitNumber);
      localStorage.setItem("mailingComponentsMazo", JSON.stringify(resResidentialAddressComponents));
    } else {
      setResMailingAddress("");
      setResMailingAddressComponents([]);
      setResMailingUnitNumber("");
      localStorage.removeItem("mailingComponentsMazo");
    }
  };

  const handleOwnSsnChange = (e) => {
    const sanitized = e.target.value.replace(/\D/g, "");
    if (sanitized.length < 10) setOwnSsn(formatDashed(sanitized, [3, 5]));
  };

  const handleOwnPercentageChange = (e) => {
    const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
    if (digitsOnly === "") return setOwnOwnershipPercentage("");
    setOwnOwnershipPercentage(Math.min(parseInt(digitsOnly, 10), 100));
  };

  const handleEqCostChange = (e) => {
    const cleaned = e.target.value.replace(/,/g, "").replace(/[^0-9.]/g, "");
    const parsed = parseInt(cleaned);
    setEqCost(isNaN(parsed) ? "" : parsed.toLocaleString());
  };

  const handleEqDepositChange = (e) => {
    const cleaned = e.target.value.replace(/,/g, "").replace(/[^0-9.]/g, "");
    const parsed = parseInt(cleaned);
    setEqDepositAmount(isNaN(parsed) ? "" : parsed.toLocaleString());
  };

  // --- Per-card validation (same rules as each field's origin step) ----
  const validateYou = () => {
    const errors = {};
    if (!youEmail || !EMAIL_REGEX.test(youEmail)) errors.email = "Please provide a valid email.";
    if (!youPhone || !PHONE_REGEX.test(youPhone)) {
      errors.phone = "Please provide a valid US phone number e.g. (234)-567-8900";
    }
    if (!youFirstName || !NAME_REGEX.test(youFirstName)) {
      errors.firstName = "Please enter a valid first name";
    }
    if (!youLastName || !NAME_REGEX.test(youLastName)) errors.lastName = "Please enter a valid last name";
    if (!isDobValid(state?.questions?.dob)) errors.dob = "Please choose a valid date.";
    if (isPersonalFlowEnabled && !youEquipmentUse) {
      errors.equipmentUse = "Please select an option.";
    }
    if (isSalesRepFlow && youHasSalesRep) {
      if (!youSalesRepFirstName || !SALES_REP_NAME_REGEX.test(youSalesRepFirstName.trim())) {
        errors.salesRepFirstName = "Please enter a valid first name (min 3 characters)";
      }
      if (!youSalesRepLastName || !SALES_REP_NAME_REGEX.test(youSalesRepLastName.trim())) {
        errors.salesRepLastName = "Please enter a valid last name (min 3 characters)";
      }
    }
    return errors;
  };

  const validateBusinessResidence = () => {
    const errors = {};
    if (isBusiness) {
      if (!bizCompanyName) errors.companyName = "Please enter your company name.";
      if (!bizEntityType) errors.entityType = "Please select an entity type.";
      if (!bizCompanyAddress || bizCompanyAddressComponents.length === 0) {
        errors.companyAddress = "Please select your company address from the list.";
      } else if (helpers.extractAddressComponents(bizCompanyAddressComponents) === false) {
        errors.companyAddress = "Please enter a valid address with state, zip code, city and country.";
      }
      if (!bizHomeAddress || bizHomeAddressComponents.length === 0) {
        errors.homeAddress = "Please select your home address from the list.";
      } else if (helpers.extractAddressComponents(bizHomeAddressComponents) === false) {
        errors.homeAddress = "Please enter a valid address with state, zip code, city and country.";
      }
      if (!bizAnnualRevenue) errors.annualRevenue = "Please provide your annual revenue.";
      if (bizLongBussiness === "" || bizLongBussiness === null || bizLongBussiness === undefined) {
        errors.longBussiness = "Please enter how long you've been in business.";
      } else if (Number(bizLongBussiness) < 1) {
        errors.longBussiness = "Years in business must be at least 1.";
      }
      if (!bizBusinessDescription || bizBusinessDescription.trim().length < 3) {
        errors.businessDescription = "Please tell us what the business does (at least 3 characters).";
      }
    } else {
      if (!resResidentialAddress || resResidentialAddressComponents.length === 0) {
        errors.residentialAddress = "Please select your address from the list.";
      } else if (helpers.extractAddressComponents(resResidentialAddressComponents) === false) {
        errors.residentialAddress = "Please enter a valid address with state, zip code, city and country.";
      }
      if (resMailingAddress && resMailingAddressComponents.length === 0) {
        errors.mailingAddress = "Please select your mailing address from the list.";
      }
      if (!resMaritalStatus) errors.maritalStatus = "Please select an option.";
      if (!resLivingYears) errors.livingYears = "This field is required.";
      if (!resLivingMonths) errors.livingMonths = "This field is required.";
      if (resLivingMonthlyPayment === "" || resLivingMonthlyPayment === null) {
        errors.livingMonthlyPayment = "This field is required.";
      }
    }
    return errors;
  };

  const validateOwnershipEmployment = () => {
    const errors = {};
    // SSN/citizenship belong to the primary applicant regardless of flow —
    // the backend's shared required-field check needs both either way.
    if (!ownSsn || !/^\d{3}-\d{2}-\d{4}$/.test(ownSsn)) {
      errors.ssn = "Please provide a valid SSN e.g. (AAA-GG-SSSS)";
    }
    if (!ownCitizenship) errors.citizenship = "Please select an option.";
    if (isBusiness) {
      if (
        ownOwnershipPercentage === "" ||
        Number(ownOwnershipPercentage) < 1 ||
        Number(ownOwnershipPercentage) > 100
      ) {
        errors.ownershipPercentage = "Enter a share between 1 and 100.";
      }
      if (ownTaxId && !/^[0-9A-Za-z]{2}-[0-9A-Za-z]{7}$/.test(ownTaxId)) {
        errors.taxId = "Please provide a valid Tax Id e.g. (xx-xxxxxxx)";
      }

      // Owner rows have their own nested per-row error map, kept in
      // ownOwnerRowErrors (mirrors StepOwnership.js's own ownerRowErrors
      // state) rather than the flat `errors` object toggleEdit renders —
      // set directly here as a side effect, same as the address-error
      // propagation toggleEdit already does for businessResidence.
      const rowErrors = {};
      if (ownHasAdditionalOwners) {
        if (ownAdditionalOwners.length === 0) {
          errors.additionalOwners = "Add at least one additional owner.";
        }
        ownAdditionalOwners.forEach((owner) => {
          const errs = {};
          if (!owner.email) errs.email = "Required.";
          if (!owner.phone || !/^\(\d{3}\)-\d{3}-\d{4}$/.test(owner.phone)) {
            errs.phone = "Please provide a valid US phone number e.g. (234)-567-8900";
          }
          if (!owner.firstName || owner.firstName.trim().length < 3) {
            errs.firstName = "Please enter a valid first name (min 3 characters).";
          }
          if (!owner.lastName || owner.lastName.trim().length < 3) {
            errs.lastName = "Please enter a valid last name (min 3 characters).";
          }
          if (!owner.dob || !dayjs(owner.dob).isValid() || dayjs(owner.dob).isAfter(dayjs())) {
            errs.dob = "Please choose a valid date.";
          }
          if (!owner.ssn || !/^[0-9A-Za-z]{3}-[0-9A-Za-z]{2}-[0-9A-Za-z]{4}$/.test(owner.ssn)) {
            errs.ssn = "Please provide a valid SSN e.g. (AAA-GG-SSSS)";
          }
          if (!owner.homeAddress || owner.homeAddressComponents.length === 0) {
            errs.homeAddress = "Please select an address from the list.";
          }
          if (
            !owner.ownershipPercentage ||
            Number(owner.ownershipPercentage) < 1 ||
            Number(owner.ownershipPercentage) > 100
          ) {
            errs.ownershipPercentage = "Enter a share between 1 and 100.";
          }
          if (Object.keys(errs).length > 0) rowErrors[owner.order] = errs;
        });
        if (ownAdditionalOwners.length > 0 && ownAllocatedShare !== Number(ownRemainingShare)) {
          errors.ownerShareSum = `Owner shares must add up to exactly 100% — currently ${ownTotalAssignedShare}%.`;
        }
      }
      setOwnOwnerRowErrors(rowErrors);
      // Always set (not just on failure) so a row that just got fixed has
      // its stale address error cleared too, matching the clear-then-
      // repopulate pattern toggleEdit uses for businessResidence. Guarded
      // against reassigning an unchanged value — updateOwnerRow creates a
      // new ownAdditionalOwners array reference, which a live-validation
      // effect (see below) also depends on, so an unconditional set here
      // would re-trigger that effect forever.
      ownAdditionalOwners.forEach((owner) => {
        const nextError = rowErrors[owner.order]?.homeAddress || "";
        if (owner.homeAddressError !== nextError) {
          updateOwnerRow(owner.order, { homeAddressError: nextError });
        }
      });
      // Not rendered anywhere — just makes sure toggleEdit's `Object.keys
      // (errors).length > 0` check still blocks saving when the only
      // problems are inside the nested per-owner-row map above, which
      // lives in ownOwnerRowErrors rather than this flat object.
      if (Object.keys(rowErrors).length > 0) errors.ownerRows = true;
    } else {
      if (!empEmploymentStatus) errors.employmentStatus = "Please select an option.";
      if (empGrossMonthlyIncome === "" || empGrossMonthlyIncome === null) {
        errors.grossMonthlyIncome = "This field is required.";
      }
      if (coAppIsCoApplicant) {
        if (!coAppFirstName || coAppFirstName.trim().length < 3) {
          errors.coApplicantFirstName = "Please enter a valid first name (min 3 characters).";
        }
        if (!coAppLastName || coAppLastName.trim().length < 3) {
          errors.coApplicantLastName = "Please enter a valid last name (min 3 characters).";
        }
        if (!coAppEmail) errors.coApplicantEmail = "Please provide a valid email.";
        // The backend requires the co-applicant's phone to create their
        // record at final submission.
        if (!coAppPhone || !/^\(\d{3}\)-\d{3}-\d{4}$/.test(coAppPhone)) {
          errors.coApplicantPhone = "Please provide a valid US phone number e.g. (234)-567-8900";
        }
        if (!coAppEmploymentStatus) errors.coApplicantEmploymentStatus = "Please select an option.";
      }
    }
    return errors;
  };

  const validateEquipment = () => {
    const errors = {};
    if (!eqCost) errors.cost = "Please provide a valid amount.";
    // Same Year/Make/Model rules as StepEquipment.js (see helpers.js).
    const yearError = helpers.getEquipmentYearError(eqYear);
    if (yearError) errors.year = yearError;
    const makeError = helpers.getEquipmentTextError("Make", eqMake);
    if (makeError) errors.make = makeError;
    const modelError = helpers.getEquipmentTextError("Model", eqModel);
    if (modelError) errors.model = modelError;
    // Matches StepEquipment.js's own validation — the live step blocks
    // Continue without a selection here, so Review's edit path shouldn't
    // silently let it through either.
    if (!eqPurchaseType) errors.purchaseType = "Please select an option.";
    const priceValue = parseInt(String(eqCost).replace(/,/g, "")) || 0;
    const depositValue = parseInt(String(eqDepositAmount).replace(/,/g, "")) || 0;
    if (depositValue > priceValue) {
      errors.deposit = "Down payment cannot be greater than the equipment price.";
    }
    return errors;
  };

  // --- Per-card save (dispatch + the same autosave POST(s) as the       --
  // --- origin step fires for these fields)                              --
  const saveYou = async () => {
    const updatedPhone = "+1" + youPhone.replace(/[^0-9]/g, "");
    dispatch({
      type: "QUESTIONS",
      payload: {
        ...state.questions,
        email: youEmail,
        phone: updatedPhone,
        firstname: youFirstName,
        lastname: youLastName,
        equipmentUse: isPersonalFlowEnabled ? youEquipmentUse : state?.questions?.equipmentUse,
        hasSalesRep: isSalesRepFlow ? youHasSalesRep : state?.questions?.hasSalesRep,
        salesRepFirstName: isSalesRepFlow && youHasSalesRep ? youSalesRepFirstName.trim() : "",
        salesRepLastName: isSalesRepFlow && youHasSalesRep ? youSalesRepLastName.trim() : "",
        purpleWaveCustomerNumber: youPurpleWaveCustomerNumber.trim(),
      },
    });
    const requests = [
      axios.post(endpoint, { email: youEmail }),
      axios.post(endpoint, { phone_no: updatedPhone }),
      axios.post(endpoint, { first_name: youFirstName }),
      axios.post(endpoint, { last_name: youLastName }),
      axios.post(endpoint, { date_of_birth: state?.questions?.dob }),
    ];
    if (isPurpleWaveListing) {
      requests.push(
        axios.post(endpoint, { purple_wave_customer_number: youPurpleWaveCustomerNumber.trim() })
      );
    }
    if (isPersonalFlowEnabled) {
      requests.push(axios.post(endpoint, { equipment_use: youEquipmentUse }));
    }
    if (isSalesRepFlow) {
      const salesRepPayload = { has_sales_rep: youHasSalesRep };
      if (youHasSalesRep) {
        salesRepPayload.sales_rep_first_name = youSalesRepFirstName.trim();
        salesRepPayload.sales_rep_last_name = youSalesRepLastName.trim();
      }
      requests.push(axios.post(endpoint, salesRepPayload));
    }
    await Promise.all(requests);
  };

  const saveBusinessResidence = async () => {
    if (isBusiness) {
      const company = helpers.extractAddressComponents(
        bizCompanyAddressComponents,
        { state: "short", route: "short" },
        bizCompanyUnitNumber
      );
      const home = helpers.extractAddressComponents(
        bizHomeAddressComponents,
        { state: "short", route: "short" },
        bizHomeUnitNumber
      );
      const parsedRevenue = parseInt(String(bizAnnualRevenue).replace(/,/g, ""));
      dispatch({
        type: "QUESTION_BUSINESS",
        payload: {
          ...state.questions.business,
          companyName: bizCompanyName,
          entityType: bizEntityType,
          companyAddress: bizCompanyAddress,
          companyUnitNumber: bizCompanyUnitNumber,
          homeAddress: bizHomeAddress,
          homeUnitNumber: bizHomeUnitNumber,
          bussinessAnual: parsedRevenue,
          longBussiness: bizLongBussiness,
          businessDescription: bizBusinessDescription,
        },
      });
      const body = {};
      if (company !== false) body.company_address = company;
      if (home !== false) body.home_address = home;
      await Promise.all([
        axios.post(endpoint, {
          company_name: bizCompanyName,
          equipment_use: state?.questions?.equipmentUse,
        }),
        axios.post(endpoint, { entity_type: bizEntityType }),
        axios.post(endpoint, { annual_business_revenue: parsedRevenue }),
        axios.post(endpoint, { business_duration: parseInt(bizLongBussiness) }),
        axios.post(endpoint, { body }),
        axios.post(endpoint, { business_description: bizBusinessDescription }),
      ]);
    } else {
      const residential = helpers.extractAddressComponents(
        resResidentialAddressComponents,
        { state: "short", route: "short" },
        resResidentialUnitNumber
      );
      const mailing = helpers.extractAddressComponents(
        resMailingAddressComponents,
        { state: "short", route: "short" },
        resMailingUnitNumber
      );
      const parsedLivingMonthlyPayment = parseInt(String(resLivingMonthlyPayment).replace(/,/g, ""));
      dispatch({
        type: "QUESTION_PERSONAL",
        payload: {
          ...state.questions.personal,
          residentialAddress: resResidentialAddress,
          residentialUnitNumber: resResidentialUnitNumber,
          mailingAddress: resMailingAddress,
          mailingUnitNumber: resMailingUnitNumber,
          maritalStatus: resMaritalStatus,
          suffix: resSuffix,
          isPlanToRegisterVehicle: resPlanToRegisterVehicle,
          livingYears: resLivingYears,
          livingMonths: resLivingMonths,
          livingMonthlyPayment: isNaN(parsedLivingMonthlyPayment) ? null : parsedLivingMonthlyPayment,
          livingStatus: resLivingStatus,
        },
      });
      const body = {
        personal_is_plan_to_register_vehicle: resPlanToRegisterVehicle,
      };
      if (residential !== false) body.personal_home_address = residential;
      if (mailing !== false) body.personal_mailing_address = mailing;
      await Promise.all([
        axios.post(endpoint, {
          personal_suffix: resSuffix,
          personal_marital_status: resMaritalStatus,
        }),
        axios.post(endpoint, { body }),
        axios.post(endpoint, {
          personal_living_years: resLivingYears,
          personal_living_months: resLivingMonths,
          personal_monthly_payment: isNaN(parsedLivingMonthlyPayment) ? null : parsedLivingMonthlyPayment,
          personal_living_status: resLivingStatus,
        }),
      ]);
    }
  };

  const saveOwnershipEmployment = async () => {
    if (isBusiness) {
      const sanitizedSsn = ownSsn.replace(/-/g, "");
      const sanitizedTaxId = ownTaxId.replace(/-/g, "");
      const remainingShare = 100 - Number(ownOwnershipPercentage);
      const ownersForState = ownHasAdditionalOwners
        ? ownAdditionalOwners.map((owner) => ({
            order: owner.order,
            email: owner.email,
            phone: "+1" + owner.phone.replace(/[^0-9]/g, ""),
            firstName: owner.firstName,
            lastName: owner.lastName,
            dob: owner.dob ? owner.dob.format("YYYY-MM-DD") : "",
            ssn: owner.ssn.replace(/-/g, ""),
            ownershipPercentage: Number(owner.ownershipPercentage),
            homeAddress: owner.homeAddress,
            homeAddressComponents: owner.homeAddressComponents,
            unitNumber: owner.unitNumber,
          }))
        : [];
      dispatch({
        type: "QUESTIONS",
        payload: { ...state.questions, ssn: sanitizedSsn, citizenship: ownCitizenship },
      });
      dispatch({
        type: "QUESTION_BUSINESS",
        payload: {
          ...state.questions.business,
          ownershipPercentage: ownOwnershipPercentage,
          taxId: sanitizedTaxId,
          additionalOwners: ownersForState,
        },
      });
      await Promise.all([
        axios.post(endpoint, { ssn_number: sanitizedSsn, citizenship: ownCitizenship }),
        axios.post(endpoint, {
          ownership_percentage: parseInt(ownOwnershipPercentage),
          second_owner_ownership_percentage: parseInt(remainingShare),
        }),
        axios.post(endpoint, { tax_id: sanitizedTaxId }),
        axios.post(endpoint, {
          additional_owners: ownersForState.map((owner) => {
            const resolvedAddress = helpers.extractAddressComponents(
              owner.homeAddressComponents,
              { state: "short", route: "short" },
              owner.unitNumber
            );
            return {
              order: owner.order,
              email: owner.email,
              phone_no: owner.phone,
              first_name: owner.firstName,
              last_name: owner.lastName,
              date_of_birth: owner.dob,
              ssn_number: owner.ssn,
              ownership_percentage: owner.ownershipPercentage,
              home_address: resolvedAddress !== false ? resolvedAddress : null,
            };
          }),
        }),
      ]);
    } else {
      const updatedBusinessPhone = empBusinessPhone ? "+1" + empBusinessPhone.replace(/[^0-9]/g, "") : "";
      const updatedCoAppPhone = coAppPhone ? "+1" + coAppPhone.replace(/[^0-9]/g, "") : "";
      const sanitizedCoAppSsn = coAppSsn.replace(/-/g, "");
      const toInt = (value) => {
        const parsed = parseInt(String(value).replace(/,/g, ""));
        return isNaN(parsed) ? null : parsed;
      };
      const sanitizedRetirementIncome = toInt(empRetirementIncome);
      const sanitizedAdditionalMonthlyIncome = toInt(empAdditionalMonthlyIncome);
      const sanitizedCoAppLivingMonthlyPayment = toInt(coAppLivingMonthlyPayment);
      const sanitizedCoAppGrossMonthlyIncome = toInt(coAppGrossMonthlyIncome);
      const sanitizedCoAppRetirementIncome = toInt(coAppRetirementIncome);
      const sanitizedCoAppAdditionalMonthlyIncome = toInt(coAppAdditionalMonthlyIncome);
      const sanitizedOwnSsn = ownSsn.replace(/-/g, "");

      dispatch({
        type: "QUESTIONS",
        payload: { ...state.questions, ssn: sanitizedOwnSsn, citizenship: ownCitizenship },
      });
      dispatch({
        type: "QUESTION_PERSONAL",
        payload: {
          ...state.questions.personal,
          employmentStatus: empEmploymentStatus,
          businessName: isWorking(empEmploymentStatus) ? empBusinessName : "",
          businessPhone: updatedBusinessPhone,
          occupationTitle: empOccupationTitle,
          employerAddress: empEmployerAddress,
          employerUnitNumber: empEmployerUnitNumber,
          retirementIncome: sanitizedRetirementIncome,
          retirementYears: empRetirementYears,
          retirementMonths: empRetirementMonths,
          grossMonthlyIncome: empGrossMonthlyIncome,
          employmentYears: empEmploymentYears,
          employmentMonths: empEmploymentMonths,
          additionalIncomeSource: empAdditionalIncomeSource,
          additionalMonthlyIncome: sanitizedAdditionalMonthlyIncome,
          isCoApplicant: coAppIsCoApplicant,
          coApplicantFirstName: coAppIsCoApplicant ? coAppFirstName : "",
          coApplicantLastName: coAppIsCoApplicant ? coAppLastName : "",
          coApplicantEmail: coAppIsCoApplicant ? coAppEmail : "",
          coApplicantPhone: coAppIsCoApplicant ? updatedCoAppPhone : "+1",
          coApplicantSsn: coAppIsCoApplicant ? sanitizedCoAppSsn : "",
          coApplicantCitizenship: coAppIsCoApplicant ? coAppCitizenship : "",
          coApplicantSuffix: coAppIsCoApplicant ? coAppSuffix : "",
          coApplicantMaritalStatus: coAppIsCoApplicant ? coAppMaritalStatus : "",
          coApplicantResidentialAddress: coAppIsCoApplicant ? coAppResidentialAddress : "",
          coApplicantResidentialUnitNumber: coAppIsCoApplicant ? coAppResidentialUnitNumber : "",
          coApplicantLivingYears: coAppIsCoApplicant ? coAppLivingYears : "",
          coApplicantLivingMonths: coAppIsCoApplicant ? coAppLivingMonths : "",
          coApplicantLivingMonthlyPayment: coAppIsCoApplicant ? sanitizedCoAppLivingMonthlyPayment : null,
          coApplicantLivingStatus: coAppIsCoApplicant ? coAppLivingStatus : "",
          coApplicantEmploymentStatus: coAppIsCoApplicant ? coAppEmploymentStatus : "",
          coApplicantBusinessName: coAppIsCoApplicant ? coAppBusinessName : "",
          coApplicantGrossMonthlyIncome: coAppIsCoApplicant ? sanitizedCoAppGrossMonthlyIncome : null,
          coApplicantEmployerAddress: coAppIsCoApplicant ? coAppEmployerAddress : "",
          coApplicantEmployerUnitNumber: coAppIsCoApplicant ? coAppEmployerUnitNumber : "",
          coApplicantretirementIncome: coAppIsCoApplicant ? sanitizedCoAppRetirementIncome : null,
          coApplicantretirementYears: coAppIsCoApplicant ? coAppRetirementYears : "",
          coApplicantretirementMonths: coAppIsCoApplicant ? coAppRetirementMonths : "",
          coApplicantEmploymentYears: coAppIsCoApplicant ? coAppEmploymentYears : "",
          coApplicantEmploymentMonths: coAppIsCoApplicant ? coAppEmploymentMonths : "",
          coApplicantAdditionalIncomeSource: coAppIsCoApplicant ? coAppAdditionalIncomeSource : "",
          coApplicantAdditionalMonthlyIncome: coAppIsCoApplicant
            ? sanitizedCoAppAdditionalMonthlyIncome
            : null,
        },
      });

      const requests = [
        axios.post(endpoint, { ssn_number: sanitizedOwnSsn, citizenship: ownCitizenship }),
        axios.post(endpoint, { personal_employment_status: empEmploymentStatus }),
      ];
      if (isWorking(empEmploymentStatus)) {
        requests.push(
          axios.post(endpoint, {
            personal_employer_business_name: empBusinessName,
            personal_employer_business_phone_no: updatedBusinessPhone,
            personal_occupation_title: empOccupationTitle,
          })
        );
        const employer = helpers.extractAddressComponents(
          empEmployerAddressComponents,
          { state: "short", route: "short" },
          empEmployerUnitNumber
        );
        const employerBody = {};
        if (employer !== false) employerBody.personal_employer_address = employer;
        requests.push(axios.post(endpoint, { body: employerBody }));
      }
      if (isRetiredIsh(empEmploymentStatus)) {
        requests.push(
          axios.post(endpoint, {
            personal_retirement_income: sanitizedRetirementIncome,
            personal_retirement_years: empRetirementYears,
            personal_retirement_months: empRetirementMonths,
          })
        );
      }
      requests.push(
        axios.post(endpoint, {
          personal_gross_monthly_income: empGrossMonthlyIncome,
          personal_employment_years: empEmploymentYears,
          personal_employment_months: empEmploymentMonths,
        })
      );
      requests.push(
        axios.post(endpoint, {
          personal_additional_income_source: empAdditionalIncomeSource,
          personal_additional_income_monthly: sanitizedAdditionalMonthlyIncome,
        })
      );

      requests.push(axios.post(endpoint, { is_co_applicant: coAppIsCoApplicant }));
      if (coAppIsCoApplicant) {
        requests.push(
          axios.post(endpoint, { personal_co_applicant_first_name: coAppFirstName }),
          axios.post(endpoint, { personal_co_applicant_last_name: coAppLastName }),
          axios.post(endpoint, { personal_co_applicant_email: coAppEmail }),
          axios.post(endpoint, { personal_co_applicant_phone_no: updatedCoAppPhone }),
          axios.post(endpoint, {
            personal_co_applicant_ssn: sanitizedCoAppSsn,
            personal_co_applicant_citizenship: coAppCitizenship,
          }),
          axios.post(endpoint, {
            // Optional — null, not "", when never picked (the backend's
            // DateField rejects "" outright).
            personal_co_applicant_date_of_birth: state?.questions?.personal?.coApplicantDob || null,
          }),
          axios.post(endpoint, {
            personal_co_applicant_suffix: coAppSuffix,
            personal_co_applicant_marital_status: coAppMaritalStatus,
          })
        );

        const coAppHome = helpers.extractAddressComponents(
          coAppResidentialAddressComponents,
          { state: "short", route: "short" },
          coAppResidentialUnitNumber
        );
        const coAppHomeBody = {};
        if (coAppHome !== false) coAppHomeBody.personal_co_applicant_home_address = coAppHome;
        requests.push(axios.post(endpoint, { body: coAppHomeBody }));

        requests.push(
          axios.post(endpoint, {
            personal_co_applicant_living_years: coAppLivingYears,
            personal_co_applicant_living_months: coAppLivingMonths,
            personal_co_applicant_monthly_payment: sanitizedCoAppLivingMonthlyPayment,
            personal_co_applicant_living_status: coAppLivingStatus,
          })
        );

        requests.push(
          axios.post(endpoint, { personal_co_applicant_employment_status: coAppEmploymentStatus })
        );

        if (isWorking(coAppEmploymentStatus)) {
          requests.push(
            axios.post(endpoint, {
              personal_co_applicant_employer_business_name: coAppBusinessName,
              personal_co_applicant_gross_monthly_income: sanitizedCoAppGrossMonthlyIncome,
            })
          );
          const coAppEmployer = helpers.extractAddressComponents(
            coAppEmployerAddressComponents,
            { state: "short", route: "short" },
            coAppEmployerUnitNumber
          );
          const coAppEmployerBody = {};
          if (coAppEmployer !== false) {
            coAppEmployerBody.personal_co_applicant_employer_address = coAppEmployer;
          }
          requests.push(axios.post(endpoint, { body: coAppEmployerBody }));
        }

        if (isRetiredIsh(coAppEmploymentStatus)) {
          requests.push(
            axios.post(endpoint, {
              personal_co_applicant_retirement_income: sanitizedCoAppRetirementIncome,
              personal_co_applicant_retirement_years: coAppRetirementYears,
              personal_co_applicant_retirement_months: coAppRetirementMonths,
            })
          );
        }

        requests.push(
          axios.post(endpoint, {
            personal_co_applicant_employment_years: coAppEmploymentYears,
            personal_co_applicant_employment_months: coAppEmploymentMonths,
          })
        );

        requests.push(
          axios.post(endpoint, {
            personal_co_applicant_additional_income_source: coAppAdditionalIncomeSource,
            personal_co_applicant_additional_income_monthly: sanitizedCoAppAdditionalMonthlyIncome,
          })
        );
      }

      await Promise.all(requests);
    }
  };

  const saveEquipment = async () => {
    const parsedCost = parseInt(String(eqCost).replace(/,/g, ""));
    const depositValue = parseInt(String(eqDepositAmount).replace(/,/g, "")) || 0;
    const depositPercent = parsedCost
      ? Math.min(Math.round((depositValue / parsedCost) * 100), 100)
      : 0;
    const equipmentName = [eqYear, eqMake, (eqModel || "").trim()].filter(Boolean).join(" ");

    // Order matters: QUESTIONS replaces `state.questions` wholesale (see
    // Reducer.js), while QUESTION_PERSONAL/QUESTION_BUSINESS only replace
    // their own nested sub-key. Firing QUESTIONS *after* the other two would
    // spread `...state.questions` from this same closure's stale snapshot —
    // taken before either of those dispatches applied — and silently
    // overwrite personal/business back to their pre-edit values, since
    // multiple dispatch() calls in one handler all read the same pre-render
    // `state`. Firing it first avoids that: it's the wholesale replace, so
    // it has nothing yet to clobber, and the two narrower dispatches after
    // it only touch their own sub-key, preserving what it just set.
    dispatch({
      type: "QUESTIONS",
      payload: {
        ...state.questions,
        equipmentCost: isNaN(parsedCost) ? null : parsedCost,
        equipmentName: equipmentName || state?.questions?.equipmentName,
        ...(showReviewPurpleWaveIcn && { purpleWaveIcn: eqPurpleWaveIcn.trim() }),
      },
    });
    dispatch({
      type: "QUESTION_PERSONAL",
      payload: {
        ...state.questions.personal,
        financingYear: eqYear,
        financingMake: eqMake,
        financingModel: eqModel,
        downPayment: depositValue,
      },
    });
    dispatch({
      type: "QUESTION_BUSINESS",
      payload: {
        ...state.questions.business,
        purchaseType: eqPurchaseType,
        replacingDescription: eqPurchaseType === "Replacement" ? eqReplacingDescription : "",
        replacingDisposition: eqPurchaseType === "Replacement" ? eqReplacingDisposition : "",
      },
    });

    const requests = [
      axios.post(endpoint, { year: eqYear }),
      axios.post(endpoint, { make: eqMake }),
      axios.post(endpoint, { model: eqModel }),
      axios.post(endpoint, { marketing_equipment_price: isNaN(parsedCost) ? null : parsedCost }),
      axios.post(endpoint, {
        down_payment_percentage: depositPercent,
        // The exact dollar figure too (the percentage is rounded), so a
        // resumed application shows the deposit actually entered.
        personal_down_payment: depositValue,
      }),
    ];
    if (showReviewPurpleWaveIcn) {
      requests.push(axios.post(endpoint, { purple_wave_icn: eqPurpleWaveIcn.trim() }));
    }
    if (eqPurchaseType) requests.push(axios.post(endpoint, { purchase_type: eqPurchaseType }));
    if (eqPurchaseType === "Replacement") {
      requests.push(
        axios.post(endpoint, {
          replacing_description: eqReplacingDescription,
          replacing_disposition: eqReplacingDisposition,
        })
      );
    }
    await Promise.all(requests);
  };

  const validators = {
    you: validateYou,
    businessResidence: validateBusinessResidence,
    ownershipEmployment: validateOwnershipEmployment,
    equipment: validateEquipment,
  };
  const savers = {
    you: saveYou,
    businessResidence: saveBusinessResidence,
    ownershipEmployment: saveOwnershipEmployment,
    equipment: saveEquipment,
  };

  const toggleEdit = async (key) => {
    if (!editing[key]) {
      setEditing((prev) => ({ ...prev, [key]: true }));
      return;
    }
    if (pendingAddressLookups > 0) return;

    // CustomAddressAutocomplete shows its own error via a dedicated prop
    // (addressValidationError), not the generic .pd6-field-error rendering
    // the other fields use — same as every origin step, clear then
    // re-populate it from this attempt's validation result.
    if (key === "businessResidence") {
      setBizCompanyAddressError("");
      setBizHomeAddressError("");
      setResResidentialAddressError("");
      setResMailingAddressError("");
    }

    const errors = validators[key]();
    if (Object.keys(errors).length > 0) {
      setBlockAttempted((prev) => ({ ...prev, [key]: true }));
      setBlockErrors((prev) => ({ ...prev, [key]: errors }));
      if (key === "businessResidence") {
        if (errors.companyAddress) setBizCompanyAddressError(errors.companyAddress);
        if (errors.homeAddress) setBizHomeAddressError(errors.homeAddress);
        if (errors.residentialAddress) setResResidentialAddressError(errors.residentialAddress);
        if (errors.mailingAddress) setResMailingAddressError(errors.mailingAddress);
      }
      return;
    }

    setSavingBlock(key);
    try {
      await savers[key]();
      setEditing((prev) => ({ ...prev, [key]: false }));
      setBlockAttempted((prev) => ({ ...prev, [key]: false }));
      setBlockErrors((prev) => ({ ...prev, [key]: {} }));
    } catch (error) {
      const parsed = helpers.parseServerFieldErrors(error?.response?.data, SERVER_FIELD_ERROR_MAP);
      if (parsed) {
        setBlockAttempted((prev) => ({ ...prev, [key]: true }));
        setBlockErrors((prev) => ({ ...prev, [key]: { ...prev[key], ...parsed.fieldErrors } }));
        setErrMsg(parsed.message);
      } else {
        setErrMsg("Something went wrong saving your changes. Please try again.");
      }
      setOpen(true);
    } finally {
      setSavingBlock(null);
    }
  };

  // Re-run each card's own validation live once a failed Done attempt has
  // set blockAttempted[key], so a field's error clears as soon as it
  // becomes valid instead of staying stuck until the next Done click —
  // same live-clear pattern every origin step (StepYou.js etc.) uses.
  useEffect(() => {
    if (!blockAttempted.you) return;
    setBlockErrors((prev) => ({ ...prev, you: validateYou() }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    blockAttempted.you,
    youEmail,
    youPhone,
    youFirstName,
    youLastName,
    state?.questions?.dob,
    youEquipmentUse,
    youHasSalesRep,
    youSalesRepFirstName,
    youSalesRepLastName,
  ]);

  useEffect(() => {
    if (!blockAttempted.businessResidence) return;
    const errors = validateBusinessResidence();
    setBlockErrors((prev) => ({ ...prev, businessResidence: errors }));
    setBizCompanyAddressError(errors.companyAddress || "");
    setBizHomeAddressError(errors.homeAddress || "");
    setResResidentialAddressError(errors.residentialAddress || "");
    setResMailingAddressError(errors.mailingAddress || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    blockAttempted.businessResidence,
    bizCompanyName,
    bizEntityType,
    bizCompanyAddress,
    bizCompanyAddressComponents,
    bizHomeAddress,
    bizHomeAddressComponents,
    bizAnnualRevenue,
    bizLongBussiness,
    bizBusinessDescription,
    resResidentialAddress,
    resResidentialAddressComponents,
    resMailingAddress,
    resMailingAddressComponents,
    resMaritalStatus,
    resLivingYears,
    resLivingMonths,
    resLivingMonthlyPayment,
  ]);

  useEffect(() => {
    if (!blockAttempted.ownershipEmployment) return;
    // validateOwnershipEmployment already sets ownOwnerRowErrors and each
    // owner row's homeAddressError as its own side effects.
    setBlockErrors((prev) => ({ ...prev, ownershipEmployment: validateOwnershipEmployment() }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    blockAttempted.ownershipEmployment,
    ownSsn,
    ownCitizenship,
    ownOwnershipPercentage,
    ownTaxId,
    ownAdditionalOwners,
    empEmploymentStatus,
    empBusinessName,
    empGrossMonthlyIncome,
    coAppIsCoApplicant,
    coAppFirstName,
    coAppLastName,
    coAppEmail,
    coAppEmploymentStatus,
  ]);

  useEffect(() => {
    if (!blockAttempted.equipment) return;
    setBlockErrors((prev) => ({ ...prev, equipment: validateEquipment() }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockAttempted.equipment, eqCost, eqYear, eqMake, eqModel, eqDepositAmount]);

  // --- Read-only display (unchanged from before, minus the equipment    --
  // --- price/deposit special-case box — those are now plain rows too)   --
  const youBlock = [
    { label: "Email", value: state?.questions?.email },
    {
      label: "Phone Number",
      value:
        state?.questions?.phone === "+1"
          ? null
          : helpers.formatUSPhoneNumber(state?.questions?.phone ?? ""),
    },
    { label: "First Name", value: state?.questions?.firstname },
    { label: "Last Name", value: state?.questions?.lastname },
    { label: "Date of Birth", value: state?.questions?.dob },
    ...(isPurpleWaveListing
      ? [{ label: "Purple Wave Customer Number", value: state?.questions?.purpleWaveCustomerNumber }]
      : []),
  ];

  // Read-only-only additions (V3: show every field the borrower saw, not a
  // curated subset) — these aren't part of any card's in-place edit form,
  // so they render as plain rows in both the read view and (via the same
  // call) alongside the edit form when that card is open. Editing them
  // still means going back to their origin step, same as before this
  // change — this only makes them visible, it doesn't add a new way to
  // change them.
  const youExtraBlock = [
    ...(Boolean(state?.isPersonalFlow)
      ? [{ label: "How will you use the equipment?", value: state?.questions?.equipmentUse }]
      : []),
    ...(Boolean(state?.isSalesRepFlow)
      ? [
          {
            label: "Has a sales representative?",
            value: state?.questions?.hasSalesRep ? "Yes" : "No",
          },
          // Only relevant once the borrower actually said "Yes" — showing
          // these as empty "—" rows when hasSalesRep is false is the same
          // "field showing when its precondition doesn't hold" bug as the
          // Employment card's employer/retirement fields below.
          ...(state?.questions?.hasSalesRep
            ? [
                { label: "Sales Rep First Name", value: state?.questions?.salesRepFirstName },
                { label: "Sales Rep Last Name", value: state?.questions?.salesRepLastName },
              ]
            : []),
        ]
      : []),
  ];

  const businessResidenceBlock = isBusiness
    ? [
        { label: "Company Name", value: state?.questions?.business?.companyName },
        { label: "Entity Type", value: state?.questions?.business?.entityType },
        { label: "Company Address", value: state?.questions?.business?.companyAddress },
        // PD-22: Apt/Suite/Unit gets its own row, shown only when actually
        // entered — never folded into the address line itself (see the
        // design discussion: inline concatenation misreads whenever the
        // borrower didn't type their own "Apt"/"Suite" designator).
        ...(state?.questions?.business?.companyUnitNumber
          ? [{ label: "Apt / Suite / Unit", value: state.questions.business.companyUnitNumber }]
          : []),
        { label: "Home Address", value: state?.questions?.business?.homeAddress },
        ...(state?.questions?.business?.homeUnitNumber
          ? [{ label: "Apt / Suite / Unit", value: state.questions.business.homeUnitNumber }]
          : []),
      ]
    : [
        { label: "Residential Address", value: state?.questions?.personal?.residentialAddress },
        ...(state?.questions?.personal?.residentialUnitNumber
          ? [{ label: "Apt / Suite / Unit", value: state.questions.personal.residentialUnitNumber }]
          : []),
        { label: "Mailing Address", value: state?.questions?.personal?.mailingAddress },
        ...(state?.questions?.personal?.mailingUnitNumber
          ? [{ label: "Apt / Suite / Unit", value: state.questions.personal.mailingUnitNumber }]
          : []),
        { label: "Marital Status", value: state?.questions?.personal?.maritalStatus },
      ];

  const businessResidenceExtraBlock = isBusiness
    ? [
        { label: "Annual Revenue", value: state?.questions?.business?.bussinessAnual },
        { label: "Years in Business", value: state?.questions?.business?.longBussiness },
        { label: "What the Business Does", value: state?.questions?.business?.businessDescription },
      ]
    : [
        { label: "Suffix", value: state?.questions?.personal?.suffix },
        {
          label: "Registering the Equipment Here?",
          value:
            state?.questions?.personal?.isPlanToRegisterVehicle === true
              ? "Yes"
              : state?.questions?.personal?.isPlanToRegisterVehicle === false
              ? "No"
              : null,
        },
        { label: "Years at Address", value: state?.questions?.personal?.livingYears },
        { label: "Months at Address", value: state?.questions?.personal?.livingMonths },
        {
          label: "Monthly Housing Payment",
          value:
            state?.questions?.personal?.livingMonthlyPayment != null
              ? `$${Number(state?.questions?.personal?.livingMonthlyPayment).toLocaleString()}`
              : null,
        },
        { label: "Housing Status", value: state?.questions?.personal?.livingStatus },
      ];

  const ownershipEmploymentBlock = isBusiness
    ? [
        { label: "SSN", value: formatSSN(state?.questions?.ssn) },
        { label: "Citizenship Status", value: state?.questions?.citizenship },
        // Same order as the Ownership step: Tax ID comes before the share.
        { label: "Tax ID", value: state?.questions?.business?.taxId },
        { label: "Ownership Percentage", value: state?.questions?.business?.ownershipPercentage },
      ]
    : [
        { label: "SSN", value: formatSSN(state?.questions?.ssn) },
        { label: "Citizenship Status", value: state?.questions?.citizenship },
        { label: "Employment Status", value: state?.questions?.personal?.employmentStatus },
        {
          label: "Gross Monthly Income",
          value:
            state?.questions?.personal?.grossMonthlyIncome != null
              ? `$${Number(state?.questions?.personal?.grossMonthlyIncome).toLocaleString()}`
              : null,
        },
      ];

  const additionalOwners = Array.isArray(state?.questions?.business?.additionalOwners)
    ? state.questions.business.additionalOwners
    : [];

  // One full-detail sub-group per additional owner (not a single collapsed
  // "Name — X%" summary line) — V3 shows every field the borrower filled
  // in for every owner, same as the co-applicant block below.
  const additionalOwnerBlocks = isBusiness
    ? additionalOwners.map((owner) => ({
        heading: `Owner ${(owner.order ?? 0) + 1}`,
        fields: [
          { label: "First Name", value: owner.firstName },
          { label: "Last Name", value: owner.lastName },
          { label: "Email", value: owner.email },
          {
            label: "Phone",
            value: owner.phone ? helpers.formatUSPhoneNumber(owner.phone) : null,
          },
          { label: "Date of Birth", value: owner.dob },
          { label: "SSN", value: formatSSN(owner.ssn) },
          {
            label: "Ownership Percentage",
            value: owner.ownershipPercentage != null ? `${owner.ownershipPercentage}%` : null,
          },
          { label: "Home Address", value: owner.homeAddress },
          ...(owner.unitNumber
            ? [{ label: "Apt / Suite / Unit", value: owner.unitNumber }]
            : []),
        ],
      }))
    : [];

  const ownershipEmploymentExtraBlock = isBusiness
    ? []
    : [
        // Employer details and retirement details only ever appear as
        // fields in the first place when the borrower's employment status
        // calls for them (isWorking / isRetiredIsh, same as the step's own
        // reveal panels) — showing them here regardless left "—" rows for
        // fields the borrower was never even asked.
        ...(isWorking(state?.questions?.personal?.employmentStatus)
          ? [
              { label: "Business Name", value: state?.questions?.personal?.businessName },
              { label: "Business Phone", value: state?.questions?.personal?.businessPhone },
              { label: "Occupation Title", value: state?.questions?.personal?.occupationTitle },
              { label: "Employer Address", value: state?.questions?.personal?.employerAddress },
              ...(state?.questions?.personal?.employerUnitNumber
                ? [{ label: "Apt / Suite / Unit", value: state.questions.personal.employerUnitNumber }]
                : []),
            ]
          : []),
        ...(isRetiredIsh(state?.questions?.personal?.employmentStatus)
          ? [
              {
                label: "Retirement Income",
                value:
                  state?.questions?.personal?.retirementIncome != null
                    ? `$${Number(state?.questions?.personal?.retirementIncome).toLocaleString()}`
                    : null,
              },
              { label: "Retirement Years", value: state?.questions?.personal?.retirementYears },
              { label: "Retirement Months", value: state?.questions?.personal?.retirementMonths },
            ]
          : []),
        { label: "Years Employed", value: state?.questions?.personal?.employmentYears },
        { label: "Months Employed", value: state?.questions?.personal?.employmentMonths },
        { label: "Other Income Source", value: state?.questions?.personal?.additionalIncomeSource },
        {
          label: "Other Monthly Income",
          value:
            state?.questions?.personal?.additionalMonthlyIncome != null
              ? `$${Number(state?.questions?.personal?.additionalMonthlyIncome).toLocaleString()}`
              : null,
        },
      ];

  const coApplicantBlock =
    !isBusiness && state?.questions?.personal?.isCoApplicant
      ? [
          { label: "First Name", value: state?.questions?.personal?.coApplicantFirstName },
          { label: "Last Name", value: state?.questions?.personal?.coApplicantLastName },
          { label: "Email", value: state?.questions?.personal?.coApplicantEmail },
          {
            label: "Phone",
            value:
              state?.questions?.personal?.coApplicantPhone === "+1"
                ? null
                : helpers.formatUSPhoneNumber(state?.questions?.personal?.coApplicantPhone ?? ""),
          },
          { label: "SSN", value: formatSSN(state?.questions?.personal?.coApplicantSsn) },
          { label: "Citizenship", value: state?.questions?.personal?.coApplicantCitizenship },
          { label: "Date of Birth", value: state?.questions?.personal?.coApplicantDob },
          { label: "Suffix", value: state?.questions?.personal?.coApplicantSuffix },
          { label: "Marital Status", value: state?.questions?.personal?.coApplicantMaritalStatus },
          { label: "Residential Address", value: state?.questions?.personal?.coApplicantResidentialAddress },
          ...(state?.questions?.personal?.coApplicantResidentialUnitNumber
            ? [{ label: "Apt / Suite / Unit", value: state.questions.personal.coApplicantResidentialUnitNumber }]
            : []),
          { label: "Years at Address", value: state?.questions?.personal?.coApplicantLivingYears },
          { label: "Months at Address", value: state?.questions?.personal?.coApplicantLivingMonths },
          {
            label: "Monthly Housing Payment",
            value:
              state?.questions?.personal?.coApplicantLivingMonthlyPayment != null
                ? `$${Number(
                    state?.questions?.personal?.coApplicantLivingMonthlyPayment
                  ).toLocaleString()}`
                : null,
          },
          { label: "Housing Status", value: state?.questions?.personal?.coApplicantLivingStatus },
          { label: "Employment Status", value: state?.questions?.personal?.coApplicantEmploymentStatus },
          {
            label: "Gross Monthly Income",
            value:
              state?.questions?.personal?.coApplicantGrossMonthlyIncome != null
                ? `$${Number(
                    state?.questions?.personal?.coApplicantGrossMonthlyIncome
                  ).toLocaleString()}`
                : null,
          },
          // Same isWorking/isRetiredIsh gating as the primary applicant's
          // own employer/retirement fields above — only relevant when the
          // co-applicant's employment status calls for them.
          ...(isWorking(state?.questions?.personal?.coApplicantEmploymentStatus)
            ? [
                { label: "Business Name", value: state?.questions?.personal?.coApplicantBusinessName },
                {
                  label: "Employer Address",
                  value: state?.questions?.personal?.coApplicantEmployerAddress,
                },
                ...(state?.questions?.personal?.coApplicantEmployerUnitNumber
                  ? [{ label: "Apt / Suite / Unit", value: state.questions.personal.coApplicantEmployerUnitNumber }]
                  : []),
              ]
            : []),
          ...(isRetiredIsh(state?.questions?.personal?.coApplicantEmploymentStatus)
            ? [
                {
                  label: "Retirement Income",
                  value:
                    state?.questions?.personal?.coApplicantretirementIncome != null
                      ? `$${Number(
                          state?.questions?.personal?.coApplicantretirementIncome
                        ).toLocaleString()}`
                      : null,
                },
                {
                  label: "Retirement Years",
                  value: state?.questions?.personal?.coApplicantretirementYears,
                },
                {
                  label: "Retirement Months",
                  value: state?.questions?.personal?.coApplicantretirementMonths,
                },
              ]
            : []),
          { label: "Years Employed", value: state?.questions?.personal?.coApplicantEmploymentYears },
          { label: "Months Employed", value: state?.questions?.personal?.coApplicantEmploymentMonths },
          {
            label: "Other Income Source",
            value: state?.questions?.personal?.coApplicantAdditionalIncomeSource,
          },
          {
            label: "Other Monthly Income",
            value:
              state?.questions?.personal?.coApplicantAdditionalMonthlyIncome != null
                ? `$${Number(
                    state?.questions?.personal?.coApplicantAdditionalMonthlyIncome
                  ).toLocaleString()}`
                : null,
          },
        ]
      : [];

  const equipmentPrice =
    state?.questions?.equipmentCost != null ? parseInt(state?.questions?.equipmentCost) : null;
  const equipmentDeposit =
    state?.questions?.personal?.downPayment != null
      ? parseInt(state?.questions?.personal?.downPayment)
      : null;

  const equipmentBlock = [
    { label: "Year", value: state?.questions?.personal?.financingYear },
    { label: "Make", value: state?.questions?.personal?.financingMake },
    { label: "Model", value: state?.questions?.personal?.financingModel },
    ...(showReviewPurpleWaveIcn
      ? [{ label: "Purple Wave Item Number", value: state?.questions?.purpleWaveIcn }]
      : []),
    { label: "Equipment Price", value: equipmentPrice != null ? `$${equipmentPrice.toLocaleString()}` : null },
    { label: "Down Payment", value: equipmentDeposit != null ? `$${equipmentDeposit.toLocaleString()}` : null },
    {
      label: "Replacement or Addition",
      value:
        state?.questions?.business?.purchaseType === "Replacement"
          ? "Replacing existing equipment"
          : state?.questions?.business?.purchaseType === "Expansion"
          ? "Adding to the fleet"
          : null,
    },
    ...(state?.questions?.business?.purchaseType === "Replacement"
      ? [
          { label: "Replacing", value: state?.questions?.business?.replacingDescription },
          { label: "Disposition", value: state?.questions?.business?.replacingDisposition },
        ]
      : []),
  ];

  // `heading` is for a sub-group inside a card (e.g. "Co-applicant",
  // "Additional owners") — renders only when at least one field in the
  // group actually has a value, so an unused optional block (co-applicant
  // toggled off, no additional owners) doesn't leave a dangling label with
  // nothing under it.
  // V3 shows every applicable field, even ones the borrower left blank
  // (rendered as "—") — it only hides a field entirely when the whole
  // group doesn't apply (e.g. no co-applicant, no additional owners), which
  // is already handled upstream by each *Block/*ExtraBlock array being
  // empty in that case.
  const renderReadFields = (fields, heading) => {
    if (fields.length === 0) return null;
    return (
      <div className="pd6-review-block__group">
        {heading && <div className="pd6-review-block__group-label">{heading}</div>}
        <div className="pd6-review-block__fields">
          {fields.map((field, index) => (
            <div className="pd6-review-field" key={`${field.label}-${index}`}>
              <span className="pd6-review-field__label">{field.label}</span>
              <span className="pd6-review-field__value">
                {field.value !== null && field.value !== undefined && field.value !== ""
                  ? String(field.value)
                  : "—"}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCardHeader = (title, key) => (
    <div className="pd6-review-block__header">
      <h4>{title}</h4>
      <button
        type="button"
        className="pd6-link-button"
        onClick={() => toggleEdit(key)}
        disabled={savingBlock === key}
      >
        {savingBlock === key ? "Saving…" : editing[key] ? "Done" : "Edit"}
      </button>
    </div>
  );

  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(",")[1]);
    const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
    return new Blob([ab], { type: mimeString });
  };

  const handleGenerateImage = () => {
    const canvas = canvasRef.current;
    if (!canvas || !name) return;
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width, canvas.height);
    // Matches the input's own plain style (see .pd6-signature-card__input in
    // stepper.css) — that field dropped its cursive/italic treatment for a
    // normal sans-serif look, so the rendered signature image (what
    // actually gets embedded in the PDF) needs to match it, not the old
    // script-font styling.
    const fontFor = (size) =>
      `600 ${size}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    // Start from the length-based size, then shrink until the whole name
    // fits inside the canvas (20px margin each side) — otherwise long names
    // are cut off in the saved image.
    let fontSize = name?.length < 15 ? 44 : name?.length < 18 ? 38 : 32;
    context.font = fontFor(fontSize);
    while (fontSize > 12 && context.measureText(name).width > canvas.width - 40) {
      fontSize -= 1;
      context.font = fontFor(fontSize);
    }
    context.fillStyle = "#212529";
    context.fillText(name, 20, 60);

    const dataURL = canvas.toDataURL("image/png");
    setImageUrl(dataURL);
    const blob = dataURItoBlob(dataURL);
    const file = new File([blob], "name_image.png", { type: "image/png" });

    dispatch({
      type: "QUESTIONS",
      payload: { ...state.questions, imageFile: file, imageName: name },
    });
  };

  useEffect(() => {
    handleGenerateImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const removeAddressComponentsFromLocalstorage = () => {
    const keysToRemove = [
      "homeComponentsMazo",
      "companyComponentsMazo",
      "secondOwnerHomeComponentsMazo",
      "residentialComponentsMazo",
      "mailingComponentsMazo",
      "employerComponentsMazo",
      "coApplicantEmployerComponentsMazo",
      "coApplicantResidentialComponentsMazo",
    ];
    keysToRemove.forEach((key) => {
      if (localStorage.getItem(key) !== null) localStorage.removeItem(key);
    });
    // StepOwnership.js's owner rows key their address components by order
    // ("additionalOwnerHomeComponentsMazo_1", "_2", ...) since there can be
    // any number of them — sweep by prefix instead of a fixed key list.
    Object.keys(localStorage)
      .filter((key) => key.startsWith("additionalOwnerHomeComponentsMazo_"))
      .forEach((key) => localStorage.removeItem(key));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || name.trim().length < 3) {
      setErrMsg("Please type your name to sign.");
      setOpen(true);
      return;
    }
    if (!agreed) {
      setErrMsg("Please agree to the Credit Authorization first.");
      setOpen(true);
      return;
    }
    if (!authorizeAgreed) {
      setErrMsg("Please check the credit authorization box to continue.");
      setOpen(true);
      return;
    }

    setShowLoader(true);
    try {
      // Same signature POST as the old SignatureComponent.js.
      await axios.post(
        `lender/partial-application/${state?.informationSlug}/?uuid=${state?.applicationUUID}`,
        { signature: state?.questions?.imageFile },
        {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        }
      );

      // Same submit-application POST as the old Confirmation.js, unchanged.
      const applicationResponse = await axios.post(
        `lender/submit-application/`,
        { uuid: state?.applicationUUID, state: state?.questions },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );

      if (applicationResponse.status === 200) {
        const applicationUUID = applicationResponse?.data?.uuid;
        const fiscoScore = applicationResponse?.data?.ficoScore;

        dispatch({ type: "APPLICATION_UUID", payload: applicationUUID });
        dispatch({
          type: "QUESTIONS",
          payload: {
            ...state?.questions,
            signedPdfUrl: applicationResponse?.data?.pdf_url,
            // PD-22: real Salesforce Owner (name/email/phone), when the async
            // sync has already populated it for this applicant — null when
            // it hasn't yet. ThanksComponent.js falls back to a generic
            // message rather than a fake name when this is null.
            fundingSpecialist: applicationResponse?.data?.funding_specialist ?? null,
          },
        });
        removeAddressComponentsFromLocalstorage();

        if (
          state?.questions?.equipmentCost > 20000 &&
          state?.questions?.equipmentUse === "Business" &&
          state?.questions?.business?.longBussiness > 2 &&
          fiscoScore > 600
        ) {
          try {
            await axios.post("lender/pull-paynet-report/", { uuid: applicationUUID });
          } catch (paynetError) {
            console.error("pull-paynet-report error:", paynetError);
          }
        }

        onSubmitted();
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      console.error("Error during application submission:", error);
      if (error?.response) {
        const fieldErrors = error?.response?.data?.Error;
        if (fieldErrors && Array.isArray(fieldErrors)) {
          // The backend returns a plain array of missing field names (e.g.
          // ["ssn_number", "citizenship"]) — not objects. Treating each
          // entry as an object (Object.keys(err)[0]) silently indexed into
          // the string's own characters instead, producing nonsense like
          // "0: s 0: c". Handle both shapes defensively, but the string
          // case is what the backend actually sends.
          const message = fieldErrors
            .map((err) =>
              typeof err === "string"
                ? err.replace(/_/g, " ")
                : `${Object.keys(err)[0]}: ${err[Object.keys(err)[0]]}`
            )
            .join(", ");
          setErrMsg(`Missing required fields: ${message || "Something went wrong."}`);
        } else {
          setErrMsg(fieldErrors || "Something went wrong. Please try again");
        }
      } else if (error?.request) {
        setErrMsg("Network error! Please check your connection.");
      } else {
        setErrMsg(error.message || "An unexpected error occurred.");
      }
      setOpen(true);
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <div className="pd6-step">
      {errMsg && (
        <AppSnackbar
          open={open}
          onClose={handleClose}
          severity="error"
          message={errMsg}
          autoHideDuration={6000}
        />
      )}

      <h2 className="pd6-step__title">Check your details and sign</h2>
      <p className="pd6-step__helper">
        Change anything that looks wrong, then sign once for the application and the credit
        authorization.
      </p>

      <div className="pd6-review-blocks">
        <div className="pd6-review-block">
          {renderCardHeader("You", "you")}
          {editing.you ? (
            <div className="pd6-review-block__edit pd6-form">
              <Form.Group className="pd6-field" controlId="reviewYouEmail">
                <Form.Label>
                  Email<span className="pd6-required">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  autoComplete="off"
                  value={youEmail}
                  onChange={(e) => setYouEmail(e.target.value)}
                  isInvalid={blockAttempted.you && Boolean(blockErrors.you.email)}
                />
                <Form.Control.Feedback type="invalid">{blockErrors.you.email}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="pd6-field" controlId="reviewYouPhone">
                <Form.Label>
                  Mobile phone<span className="pd6-required">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  inputMode="numeric"
                  value={youPhone}
                  onChange={handleYouPhoneChange}
                  isInvalid={blockAttempted.you && Boolean(blockErrors.you.phone)}
                />
                <Form.Control.Feedback type="invalid">{blockErrors.you.phone}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="pd6-field" controlId="reviewYouDob">
                <Form.Label>
                  Date of birth<span className="pd6-required">*</span>
                </Form.Label>
                <Birthday />
                {blockAttempted.you && blockErrors.you.dob && (
                  <div className="pd6-field-error">{blockErrors.you.dob}</div>
                )}
              </Form.Group>
              <Form.Group className="pd6-field" controlId="reviewYouFirstName">
                <Form.Label>
                  First name<span className="pd6-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  autoComplete="off"
                  value={youFirstName}
                  onChange={(e) => setYouFirstName(e.target.value)}
                  isInvalid={blockAttempted.you && Boolean(blockErrors.you.firstName)}
                />
                <Form.Control.Feedback type="invalid">{blockErrors.you.firstName}</Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="pd6-field" controlId="reviewYouLastName">
                <Form.Label>
                  Last name<span className="pd6-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  autoComplete="off"
                  value={youLastName}
                  onChange={(e) => setYouLastName(e.target.value)}
                  isInvalid={blockAttempted.you && Boolean(blockErrors.you.lastName)}
                />
                <Form.Control.Feedback type="invalid">{blockErrors.you.lastName}</Form.Control.Feedback>
              </Form.Group>
              {isPurpleWaveListing && (
                <Form.Group className="pd6-field" controlId="reviewYouPurpleWaveCustomerNumber">
                  <Form.Label>Purple Wave Customer Number</Form.Label>
                  <Form.Control
                    type="text"
                    autoComplete="off"
                    value={youPurpleWaveCustomerNumber}
                    onChange={(e) => setYouPurpleWaveCustomerNumber(e.target.value)}
                  />
                  <div className="pd6-field-helper">
                    <span>
                      Optional — found in your Purple Wave account. Adding it helps us confirm your bid and move your approval faster.
                    </span>
                  </div>
                </Form.Group>
              )}
              {isPersonalFlowEnabled && (
                <Form.Group className="pd6-field" controlId="reviewYouEquipmentUse">
                  <Form.Label>
                    How will you use the equipment?<span className="pd6-required">*</span>
                  </Form.Label>
                  <div className="pd6-chip-group">
                    <button
                      type="button"
                      className={`pd6-chip ${youEquipmentUse === "Business" ? "pd6-chip--selected" : ""}`}
                      onClick={() => setYouEquipmentUse("Business")}
                    >
                      Business
                    </button>
                    <button
                      type="button"
                      className={`pd6-chip ${youEquipmentUse === "Personal" ? "pd6-chip--selected" : ""}`}
                      onClick={() => setYouEquipmentUse("Personal")}
                    >
                      Personal
                    </button>
                  </div>
                  {blockAttempted.you && blockErrors.you.equipmentUse && (
                    <div className="pd6-field-error">{blockErrors.you.equipmentUse}</div>
                  )}
                </Form.Group>
              )}
              {isSalesRepFlow && (
                <Form.Group className="pd6-field" controlId="reviewYouHasSalesRep">
                  <Form.Label>
                    Do you have a sales representative you are working with?
                    <span className="pd6-required">*</span>
                  </Form.Label>
                  <div className="pd6-chip-group">
                    <button
                      type="button"
                      className={`pd6-chip ${youHasSalesRep ? "pd6-chip--selected" : ""}`}
                      onClick={() => handleYouSalesRepOptionChange(true)}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`pd6-chip ${!youHasSalesRep ? "pd6-chip--selected" : ""}`}
                      onClick={() => handleYouSalesRepOptionChange(false)}
                    >
                      No
                    </button>
                  </div>
                  {youHasSalesRep && (
                    <div className="pd6-reveal-panel">
                      <div className="pd6-field-row">
                        <Form.Group className="pd6-field" controlId="reviewYouSalesRepFirstName">
                          <Form.Label>
                            Representative first name<span className="pd6-required">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={youSalesRepFirstName}
                            onChange={(e) => setYouSalesRepFirstName(e.target.value)}
                            isInvalid={blockAttempted.you && Boolean(blockErrors.you.salesRepFirstName)}
                          />
                          <Form.Control.Feedback type="invalid">
                            {blockErrors.you.salesRepFirstName}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="pd6-field" controlId="reviewYouSalesRepLastName">
                          <Form.Label>
                            Representative last name<span className="pd6-required">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={youSalesRepLastName}
                            onChange={(e) => setYouSalesRepLastName(e.target.value)}
                            isInvalid={blockAttempted.you && Boolean(blockErrors.you.salesRepLastName)}
                          />
                          <Form.Control.Feedback type="invalid">
                            {blockErrors.you.salesRepLastName}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </div>
                    </div>
                  )}
                </Form.Group>
              )}
            </div>
          ) : (
            <>
              {renderReadFields(youBlock)}
              {renderReadFields(youExtraBlock)}
            </>
          )}
        </div>

        <div className="pd6-review-block">
          {renderCardHeader(isBusiness ? "Business" : "Residence", "businessResidence")}
          {editing.businessResidence ? (
            <div className="pd6-review-block__edit pd6-form">
              {isBusiness ? (
                <>
                  <Form.Group className="pd6-field" controlId="reviewBizCompanyName">
                    <Form.Label>
                      Company name<span className="pd6-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      autoComplete="off"
                      value={bizCompanyName}
                      onChange={(e) => setBizCompanyName(e.target.value)}
                      isInvalid={
                        blockAttempted.businessResidence && Boolean(blockErrors.businessResidence.companyName)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {blockErrors.businessResidence.companyName}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="reviewBizEntityType">
                    <Form.Label>
                      Entity type<span className="pd6-required">*</span>
                    </Form.Label>
                    <div className="pd6-chip-group">
                      {ENTITY_TYPE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`pd6-chip ${bizEntityType === option.value ? "pd6-chip--selected" : ""}`}
                          onClick={() => setBizEntityType(option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    {blockAttempted.businessResidence && blockErrors.businessResidence.entityType && (
                      <div className="pd6-field-error">{blockErrors.businessResidence.entityType}</div>
                    )}
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="reviewBizCompanyAddress">
                    <CustomAddressAutocomplete
                      label="Company address"
                      addressSearchQuery={bizCompanyAddress}
                      // Keep a mirrored (and therefore locked) home address
                      // in sync, same as StepBusiness.js.
                      setAddressSearchQuery={(value) => {
                        setBizCompanyAddress(value);
                        if (bizSameAsCompanyAddress) setBizHomeAddress(value);
                      }}
                      setAddressComponents={(components) => {
                        setBizCompanyAddressComponents(components);
                        if (bizSameAsCompanyAddress) {
                          setBizHomeAddressComponents(components);
                          localStorage.setItem("homeComponentsMazo", JSON.stringify(components));
                        }
                      }}
                      addressValidationError={blockAttempted.businessResidence ? bizCompanyAddressError : ""}
                      setAddressValidationError={setBizCompanyAddressError}
                      placeholder="company address"
                      localStorageLabel="companyComponentsMazo"
                      isRequired={true}
                      onResolvingChange={handleAddressResolvingChange}
                    />
                    <Form.Control
                      type="text"
                      className="pd6-address-unit-input"
                      placeholder="Apt, suite, or unit (optional)"
                      autoComplete="off"
                      value={bizCompanyUnitNumber}
                      onChange={(e) => {
                        setBizCompanyUnitNumber(e.target.value);
                        if (bizSameAsCompanyAddress) setBizHomeUnitNumber(e.target.value);
                      }}
                    />
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="reviewBizHomeAddress">
                    <CustomAddressAutocomplete
                      label="Home address"
                      addressSearchQuery={bizHomeAddress}
                      setAddressSearchQuery={setBizHomeAddress}
                      setAddressComponents={setBizHomeAddressComponents}
                      addressValidationError={blockAttempted.businessResidence ? bizHomeAddressError : ""}
                      setAddressValidationError={setBizHomeAddressError}
                      placeholder="home address"
                      localStorageLabel="homeComponentsMazo"
                      isRequired={true}
                      onResolvingChange={handleAddressResolvingChange}
                      disabled={bizSameAsCompanyAddress}
                    />
                    <Form.Control
                      type="text"
                      className="pd6-address-unit-input"
                      placeholder="Apt, suite, or unit (optional)"
                      autoComplete="off"
                      value={bizHomeUnitNumber}
                      onChange={(e) => setBizHomeUnitNumber(e.target.value)}
                      disabled={bizSameAsCompanyAddress}
                    />
                    <div className="pd6-checkbox-row">
                      <input
                        type="checkbox"
                        id="reviewBizSameAddress"
                        checked={bizSameAsCompanyAddress}
                        disabled={!bizCompanyAddress}
                        onChange={handleBizSameAsCompanyAddressChange}
                      />
                      <label htmlFor="reviewBizSameAddress">Same as company address</label>
                    </div>
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="reviewBizAnnualRevenue">
                    <Form.Label>
                      Annual revenue<span className="pd6-required">*</span>
                    </Form.Label>
                    <div className="pd6-currency-input">
                      <span className="pd6-currency-input__prefix">$</span>
                      <Form.Control
                        type="text"
                        placeholder="0"
                        autoComplete="off"
                        value={bizAnnualRevenue}
                        onChange={handleCurrencyChange(setBizAnnualRevenue)}
                        isInvalid={
                          blockAttempted.businessResidence && Boolean(blockErrors.businessResidence.annualRevenue)
                        }
                      />
                    </div>
                    {blockAttempted.businessResidence && blockErrors.businessResidence.annualRevenue && (
                      <div className="pd6-field-error">{blockErrors.businessResidence.annualRevenue}</div>
                    )}
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="reviewBizDuration">
                    <Form.Label>
                      Years in business<span className="pd6-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      placeholder="e.g. 22"
                      autoComplete="off"
                      value={bizLongBussiness}
                      onChange={handleBizLongBussinessChange}
                      isInvalid={
                        blockAttempted.businessResidence &&
                        Boolean(blockErrors.businessResidence.longBussiness)
                      }
                    />
                    {blockAttempted.businessResidence && blockErrors.businessResidence.longBussiness && (
                      <div className="pd6-field-error">{blockErrors.businessResidence.longBussiness}</div>
                    )}
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="reviewBizDescription">
                    <Form.Label>
                    What does the business do?<span className="pd6-required">*</span>
                  </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={bizBusinessDescription}
                      onChange={(e) => setBizBusinessDescription(e.target.value)}
                      isInvalid={
                        blockAttempted.businessResidence &&
                        Boolean(blockErrors.businessResidence.businessDescription)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {blockErrors.businessResidence.businessDescription}
                    </Form.Control.Feedback>
                  </Form.Group>
                </>
              ) : (
                <>
                  <Form.Group className="pd6-field" controlId="reviewResResidentialAddress">
                    <CustomAddressAutocomplete
                      label="Residential address"
                      addressSearchQuery={resResidentialAddress}
                      // Keep a mirrored (and therefore locked) mailing
                      // address in sync, same as StepResidence.js.
                      setAddressSearchQuery={(value) => {
                        setResResidentialAddress(value);
                        if (resSameAsResidential) setResMailingAddress(value);
                      }}
                      setAddressComponents={(components) => {
                        setResResidentialAddressComponents(components);
                        if (resSameAsResidential) {
                          setResMailingAddressComponents(components);
                          localStorage.setItem("mailingComponentsMazo", JSON.stringify(components));
                        }
                      }}
                      addressValidationError={
                        blockAttempted.businessResidence ? resResidentialAddressError : ""
                      }
                      setAddressValidationError={setResResidentialAddressError}
                      placeholder="residential address"
                      localStorageLabel="residentialComponentsMazo"
                      isRequired={true}
                      onResolvingChange={handleAddressResolvingChange}
                    />
                    <Form.Control
                      type="text"
                      className="pd6-address-unit-input"
                      placeholder="Apt, suite, or unit (optional)"
                      autoComplete="off"
                      value={resResidentialUnitNumber}
                      onChange={(e) => {
                        setResResidentialUnitNumber(e.target.value);
                        if (resSameAsResidential) setResMailingUnitNumber(e.target.value);
                      }}
                    />
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="reviewResMailingAddress">
                    <CustomAddressAutocomplete
                      label="Mailing address"
                      addressSearchQuery={resMailingAddress}
                      setAddressSearchQuery={setResMailingAddress}
                      setAddressComponents={setResMailingAddressComponents}
                      addressValidationError={blockAttempted.businessResidence ? resMailingAddressError : ""}
                      setAddressValidationError={setResMailingAddressError}
                      placeholder="mailing address"
                      localStorageLabel="mailingComponentsMazo"
                      isRequired={false}
                      onResolvingChange={handleAddressResolvingChange}
                      disabled={resSameAsResidential}
                    />
                    <Form.Control
                      type="text"
                      className="pd6-address-unit-input"
                      placeholder="Apt, suite, or unit (optional)"
                      autoComplete="off"
                      value={resMailingUnitNumber}
                      onChange={(e) => setResMailingUnitNumber(e.target.value)}
                      disabled={resSameAsResidential}
                    />
                    <div className="pd6-checkbox-row">
                      <input
                        type="checkbox"
                        id="reviewResSameAddress"
                        checked={resSameAsResidential}
                        disabled={!resResidentialAddress}
                        onChange={handleResSameAsResidentialChange}
                      />
                      <label htmlFor="reviewResSameAddress">Same as residential address</label>
                    </div>
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="reviewResMaritalStatus">
                    <Form.Label>
                      Marital status<span className="pd6-required">*</span>
                    </Form.Label>
                    <div className="pd6-chip-group">
                      {MARITAL_STATUS_OPTIONS.map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`pd6-chip ${resMaritalStatus === option ? "pd6-chip--selected" : ""}`}
                          onClick={() => setResMaritalStatus(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                    {blockAttempted.businessResidence && blockErrors.businessResidence.maritalStatus && (
                      <div className="pd6-field-error">{blockErrors.businessResidence.maritalStatus}</div>
                    )}
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="reviewResSuffix">
                    <Form.Label>Suffix</Form.Label>
                    <div className="pd6-chip-group">
                      {SUFFIX_OPTIONS.map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`pd6-chip ${resSuffix === option ? "pd6-chip--selected" : ""}`}
                          onClick={() => setResSuffix(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="reviewResPlanToRegister">
                    <Form.Label>
                      Registering the equipment here?<span className="pd6-required">*</span>
                    </Form.Label>
                    <div className="pd6-chip-group">
                      <button
                        type="button"
                        className={`pd6-chip ${resPlanToRegisterVehicle ? "pd6-chip--selected" : ""}`}
                        onClick={() => setResPlanToRegisterVehicle(true)}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        className={`pd6-chip ${!resPlanToRegisterVehicle ? "pd6-chip--selected" : ""}`}
                        onClick={() => setResPlanToRegisterVehicle(false)}
                      >
                        No
                      </button>
                    </div>
                  </Form.Group>
                  <div className="pd6-field-row">
                    <Form.Group className="pd6-field" controlId="reviewResLivingYears">
                      <Form.Label>
                        Years at address<span className="pd6-required">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        autoComplete="off"
                        value={resLivingYears}
                        onChange={(e) => setResLivingYears(e.target.value)}
                        isInvalid={
                          blockAttempted.businessResidence && Boolean(blockErrors.businessResidence.livingYears)
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {blockErrors.businessResidence.livingYears}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="pd6-field" controlId="reviewResLivingMonths">
                      <Form.Label>
                        Months at address<span className="pd6-required">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        autoComplete="off"
                        value={resLivingMonths}
                        onChange={(e) => setResLivingMonths(e.target.value)}
                        isInvalid={
                          blockAttempted.businessResidence && Boolean(blockErrors.businessResidence.livingMonths)
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {blockErrors.businessResidence.livingMonths}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className="pd6-field-row">
                    <Form.Group className="pd6-field" controlId="reviewResMonthlyPayment">
                      <Form.Label>
                        Monthly housing payment<span className="pd6-required">*</span>
                      </Form.Label>
                      <div className="pd6-currency-input">
                        <span className="pd6-currency-input__prefix">$</span>
                        <Form.Control
                          type="text"
                          placeholder="0"
                          value={resLivingMonthlyPayment}
                          onChange={handleCurrencyChange(setResLivingMonthlyPayment)}
                          isInvalid={
                            blockAttempted.businessResidence &&
                            Boolean(blockErrors.businessResidence.livingMonthlyPayment)
                          }
                        />
                      </div>
                      {blockAttempted.businessResidence &&
                        blockErrors.businessResidence.livingMonthlyPayment && (
                          <div className="pd6-field-error">
                            {blockErrors.businessResidence.livingMonthlyPayment}
                          </div>
                        )}
                    </Form.Group>
                    <Form.Group className="pd6-field" controlId="reviewResHousingStatus">
                      <Form.Label>Housing status</Form.Label>
                      <div className="pd6-chip-group pd6-chip-group--bands">
                        {HOUSING_STATUS_OPTIONS.map((option) => (
                          <button
                            key={option}
                            type="button"
                            className={`pd6-chip pd6-chip--band ${
                              resLivingStatus === option ? "pd6-chip--selected" : ""
                            }`}
                            onClick={() => setResLivingStatus(option)}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </Form.Group>
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              {renderReadFields(businessResidenceBlock)}
              {renderReadFields(businessResidenceExtraBlock)}
            </>
          )}
        </div>

        <div className="pd6-review-block">
          {renderCardHeader(isBusiness ? "Ownership" : "Employment & Income", "ownershipEmployment")}
          {editing.ownershipEmployment ? (
            <div className="pd6-review-block__edit pd6-form">
              {isBusiness ? (
                <>
                  <Form.Group className="pd6-field" controlId="reviewOwnSsn">
                    <Form.Label>
                      Social security number<span className="pd6-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={ownSsn}
                      onChange={handleOwnSsnChange}
                      isInvalid={
                        blockAttempted.ownershipEmployment && Boolean(blockErrors.ownershipEmployment.ssn)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {blockErrors.ownershipEmployment.ssn}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="reviewOwnCitizenship">
                    <Form.Label>
                      Citizenship<span className="pd6-required">*</span>
                    </Form.Label>
                    <div className="pd6-chip-group">
                      {CITIZENSHIP_OPTIONS.map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`pd6-chip ${ownCitizenship === option ? "pd6-chip--selected" : ""}`}
                          onClick={() => setOwnCitizenship(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                    {blockAttempted.ownershipEmployment && blockErrors.ownershipEmployment.citizenship && (
                      <div className="pd6-field-error">{blockErrors.ownershipEmployment.citizenship}</div>
                    )}
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="reviewOwnTaxId">
                    <Form.Label>Tax ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="12-3456789"
                      autoComplete="off"
                      value={ownTaxId}
                      onChange={(e) => {
                        const sanitized = e.target.value.replace(/[^A-Za-z0-9]/g, "");
                        if (sanitized.length < 10) setOwnTaxId(formatDashed(sanitized, [2]));
                      }}
                      isInvalid={
                        blockAttempted.ownershipEmployment && Boolean(blockErrors.ownershipEmployment.taxId)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {blockErrors.ownershipEmployment.taxId}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="pd6-field" controlId="reviewOwnPercentage">
                    <Form.Label>
                      Ownership Percentage<span className="pd6-required">*</span>
                    </Form.Label>
                    <div className="pd6-percent-input">
                      <Form.Control
                        type="tel"
                        inputMode="numeric"
                        value={ownOwnershipPercentage}
                        onChange={handleOwnPercentageChange}
                        isInvalid={
                          blockAttempted.ownershipEmployment &&
                          Boolean(blockErrors.ownershipEmployment.ownershipPercentage)
                        }
                      />
                      <span className="pd6-percent-input__suffix">%</span>
                    </div>
                    {blockAttempted.ownershipEmployment &&
                      blockErrors.ownershipEmployment.ownershipPercentage && (
                        <div className="pd6-field-error">
                          {blockErrors.ownershipEmployment.ownershipPercentage}
                        </div>
                      )}
                    <div style={{ fontSize: "12px", color: "#8a8a92", marginTop: "6px", lineHeight: 1.45 }}>
                      Whole numbers, 1 to 100.
                    </div>
                    {ownHasAdditionalOwners && (
                      <div style={{ fontSize: "12px", color: "#8a8a92", marginTop: "6px", lineHeight: 1.45 }}>
                        Remaining share: <strong>{ownTotalUnassignedShare}%</strong> to assign —{" "}
                        <strong>{ownTotalAssignedShare}%</strong> assigned so far
                      </div>
                    )}
                  </Form.Group>

                  {ownHasAdditionalOwners &&
                    ownAdditionalOwners.map((owner, index) => (
                      <div className="pd6-reveal-panel" key={owner.order}>
                        <div className="pd6-reveal-panel__header">
                          <span className="pd6-reveal-panel__tag">
                            {index === 0 ? "Added automatically" : `Owner ${owner.order + 1} of the business`}
                          </span>
                          <span style={{ fontSize: "14px", fontWeight: 600 }}>
                            {index === 0 ? "The rest of the business — who holds it?" : "Another owner"}
                          </span>
                          <button
                            type="button"
                            className="pd6-btn pd6-btn--ghost pd6-btn--small"
                            onClick={() => removeOwnerRow(owner.order)}
                          >
                            Remove
                          </button>
                        </div>

                        <Form.Group className="pd6-field mt-3" controlId={`reviewOwnerShare-${owner.order}`}>
                          <Form.Label>
                            This owner's share<span className="pd6-required">*</span>
                          </Form.Label>
                          <div className="pd6-percent-input">
                            <Form.Control
                              type="tel"
                              inputMode="numeric"
                              placeholder="0"
                              value={owner.ownershipPercentage}
                              onChange={(e) => {
                                const digitsOnly = e.target.value.replace(/[^0-9]/g, "");
                                updateOwnerRow(owner.order, {
                                  ownershipPercentage:
                                    digitsOnly === "" ? "" : Math.min(parseInt(digitsOnly, 10), 100),
                                });
                              }}
                              isInvalid={
                                blockAttempted.ownershipEmployment &&
                                Boolean(ownOwnerRowErrors[owner.order]?.ownershipPercentage)
                              }
                            />
                            <span className="pd6-percent-input__suffix">%</span>
                          </div>
                          {blockAttempted.ownershipEmployment &&
                            ownOwnerRowErrors[owner.order]?.ownershipPercentage && (
                              <div className="pd6-field-error">
                                {ownOwnerRowErrors[owner.order]?.ownershipPercentage}
                              </div>
                            )}
                        </Form.Group>

                        <Form.Group className="pd6-field" controlId={`reviewOwnerEmail-${owner.order}`}>
                          <Form.Label>
                            Email<span className="pd6-required">*</span>
                          </Form.Label>
                          <Form.Control
                            type="email"
                            autoComplete="off"
                            placeholder="name@example.com"
                            value={owner.email}
                            onChange={(e) => updateOwnerRow(owner.order, { email: e.target.value })}
                            isInvalid={
                              blockAttempted.ownershipEmployment &&
                              Boolean(ownOwnerRowErrors[owner.order]?.email)
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {ownOwnerRowErrors[owner.order]?.email}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="pd6-field" controlId={`reviewOwnerPhone-${owner.order}`}>
                          <Form.Label>
                            Phone<span className="pd6-required">*</span>
                          </Form.Label>
                          <Form.Control
                            type="tel"
                            inputMode="numeric"
                            placeholder="(555)-555-5555"
                            value={owner.phone}
                            onChange={(e) => handleOwnerPhoneChange(owner.order, e)}
                            isInvalid={
                              blockAttempted.ownershipEmployment &&
                              Boolean(ownOwnerRowErrors[owner.order]?.phone)
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {ownOwnerRowErrors[owner.order]?.phone}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <div className="pd6-field-row">
                          <Form.Group className="pd6-field" controlId={`reviewOwnerFirstName-${owner.order}`}>
                            <Form.Label>
                              First name<span className="pd6-required">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={owner.firstName}
                              onChange={(e) => updateOwnerRow(owner.order, { firstName: e.target.value })}
                              isInvalid={
                                blockAttempted.ownershipEmployment &&
                                Boolean(ownOwnerRowErrors[owner.order]?.firstName)
                              }
                            />
                            <Form.Control.Feedback type="invalid">
                              {ownOwnerRowErrors[owner.order]?.firstName}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <Form.Group className="pd6-field" controlId={`reviewOwnerLastName-${owner.order}`}>
                            <Form.Label>
                              Last name<span className="pd6-required">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={owner.lastName}
                              onChange={(e) => updateOwnerRow(owner.order, { lastName: e.target.value })}
                              isInvalid={
                                blockAttempted.ownershipEmployment &&
                                Boolean(ownOwnerRowErrors[owner.order]?.lastName)
                              }
                            />
                            <Form.Control.Feedback type="invalid">
                              {ownOwnerRowErrors[owner.order]?.lastName}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </div>

                        <Form.Group className="pd6-field" controlId={`reviewOwnerDob-${owner.order}`}>
                          <Form.Label>
                            Date of birth<span className="pd6-required">*</span>
                          </Form.Label>
                          <SecondOwnerBirthday
                            value={owner.dob}
                            onChange={(value) => updateOwnerRow(owner.order, { dob: value })}
                          />
                          {blockAttempted.ownershipEmployment && ownOwnerRowErrors[owner.order]?.dob && (
                            <div className="pd6-field-error">{ownOwnerRowErrors[owner.order].dob}</div>
                          )}
                        </Form.Group>

                        <Form.Group className="pd6-field" controlId={`reviewOwnerSsn-${owner.order}`}>
                          <Form.Label>
                            SSN<span className="pd6-required">*</span>
                          </Form.Label>
                          <Form.Control
                            type="tel"
                            inputMode="numeric"
                            placeholder="123-45-6789"
                            value={owner.ssn}
                            onChange={(e) => handleOwnerSSNChange(owner.order, e)}
                            isInvalid={
                              blockAttempted.ownershipEmployment &&
                              Boolean(ownOwnerRowErrors[owner.order]?.ssn)
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {ownOwnerRowErrors[owner.order]?.ssn}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="pd6-field" controlId={`reviewOwnerHomeAddress-${owner.order}`}>
                          <CustomAddressAutocomplete
                            label="Home address"
                            addressSearchQuery={owner.homeAddress}
                            setAddressSearchQuery={(value) => updateOwnerRow(owner.order, { homeAddress: value })}
                            setAddressComponents={(components) =>
                              updateOwnerRow(owner.order, { homeAddressComponents: components })
                            }
                            addressValidationError={
                              blockAttempted.ownershipEmployment ? owner.homeAddressError : ""
                            }
                            setAddressValidationError={(value) =>
                              updateOwnerRow(owner.order, { homeAddressError: value })
                            }
                            placeholder="home address"
                            localStorageLabel={`additionalOwnerHomeComponentsMazo_${owner.order}`}
                            isRequired={true}
                            onResolvingChange={handleAddressResolvingChange}
                          />
                          <Form.Control
                            type="text"
                            className="pd6-address-unit-input"
                            placeholder="Apt, suite, or unit (optional)"
                            autoComplete="off"
                            value={owner.unitNumber ?? ""}
                            onChange={(e) => updateOwnerRow(owner.order, { unitNumber: e.target.value })}
                          />
                        </Form.Group>
                      </div>
                    ))}

                  {ownHasAdditionalOwners && (
                    <div className="pd6-field mb-3" style={{ marginTop: "16px" }}>
                      <button
                        type="button"
                        className="pd6-btn pd6-btn--ghost pd6-btn--small pd6-btn--add-owner"
                        onClick={addOwnerRow}
                      >
                        + Add another owner
                      </button>
                      {blockAttempted.ownershipEmployment && blockErrors.ownershipEmployment.ownerShareSum && (
                        <div className="pd6-field-error">{blockErrors.ownershipEmployment.ownerShareSum}</div>
                      )}
                      {blockAttempted.ownershipEmployment &&
                        blockErrors.ownershipEmployment.additionalOwners && (
                          <div className="pd6-field-error">
                            {blockErrors.ownershipEmployment.additionalOwners}
                          </div>
                        )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <Form.Group className="pd6-field" controlId="reviewEmpSsn">
                    <Form.Label>
                      Social security number<span className="pd6-required">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={ownSsn}
                      onChange={handleOwnSsnChange}
                      isInvalid={
                        blockAttempted.ownershipEmployment && Boolean(blockErrors.ownershipEmployment.ssn)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {blockErrors.ownershipEmployment.ssn}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="reviewEmpCitizenship">
                    <Form.Label>
                      Citizenship<span className="pd6-required">*</span>
                    </Form.Label>
                    <div className="pd6-chip-group">
                      {CITIZENSHIP_OPTIONS.map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`pd6-chip ${ownCitizenship === option ? "pd6-chip--selected" : ""}`}
                          onClick={() => setOwnCitizenship(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                    {blockAttempted.ownershipEmployment && blockErrors.ownershipEmployment.citizenship && (
                      <div className="pd6-field-error">{blockErrors.ownershipEmployment.citizenship}</div>
                    )}
                  </Form.Group>
                  <Form.Group className="pd6-field" controlId="reviewEmpStatus">
                    <Form.Label>
                      Employment status<span className="pd6-required">*</span>
                    </Form.Label>
                    <div className="pd6-chip-group">
                      {EMPLOYMENT_STATUS_OPTIONS.map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`pd6-chip ${empEmploymentStatus === option ? "pd6-chip--selected" : ""}`}
                          onClick={() => setEmpEmploymentStatus(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                    {blockAttempted.ownershipEmployment &&
                      blockErrors.ownershipEmployment.employmentStatus && (
                        <div className="pd6-field-error">
                          {blockErrors.ownershipEmployment.employmentStatus}
                        </div>
                      )}
                  </Form.Group>
                  {isWorking(empEmploymentStatus) && (
                    <div className="pd6-reveal-panel">
                      <span className="pd6-reveal-panel__tag">Employer details</span>
                      <div className="pd6-field-row mt-3">
                        <Form.Group className="pd6-field" controlId="reviewEmpBusinessName">
                          <Form.Label>Business name</Form.Label>
                          <Form.Control
                            type="text"
                            value={empBusinessName}
                            onChange={(e) => setEmpBusinessName(e.target.value)}
                          />
                        </Form.Group>
                        <Form.Group className="pd6-field" controlId="reviewEmpOccupation">
                          <Form.Label>Occupation title</Form.Label>
                          <Form.Control
                            type="text"
                            value={empOccupationTitle}
                            onChange={(e) => setEmpOccupationTitle(e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <Form.Group className="pd6-field" controlId="reviewEmpBusinessPhone">
                        <Form.Label>Business phone</Form.Label>
                        <Form.Control
                          type="tel"
                          inputMode="numeric"
                          placeholder="(555)-555-5555"
                          value={empBusinessPhone}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/[^0-9]/g, "");
                            if (digits.length <= 10) setEmpBusinessPhone(formatPhoneNumber(digits));
                          }}
                        />
                      </Form.Group>
                      <Form.Group className="pd6-field" controlId="reviewEmpEmployerAddress">
                        <CustomAddressAutocomplete
                          label="Employer address"
                          addressSearchQuery={empEmployerAddress}
                          setAddressSearchQuery={setEmpEmployerAddress}
                          setAddressComponents={setEmpEmployerAddressComponents}
                          addressValidationError={empEmployerAddressError}
                          setAddressValidationError={setEmpEmployerAddressError}
                          placeholder="employer address"
                          localStorageLabel="employerComponentsMazo"
                          isRequired={false}
                          onResolvingChange={handleAddressResolvingChange}
                        />
                        <Form.Control
                          type="text"
                          className="pd6-address-unit-input"
                          placeholder="Apt, suite, or unit (optional)"
                          autoComplete="off"
                          value={empEmployerUnitNumber}
                          onChange={(e) => setEmpEmployerUnitNumber(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  )}
                  {isRetiredIsh(empEmploymentStatus) && (
                    <div className="pd6-reveal-panel">
                      <span className="pd6-reveal-panel__tag">Retirement details</span>
                      <div className="pd6-field-row mt-3">
                        <Form.Group className="pd6-field" controlId="reviewEmpRetirementIncome">
                          <Form.Label>Retirement income</Form.Label>
                          <div className="pd6-currency-input">
                            <span className="pd6-currency-input__prefix">$</span>
                            <Form.Control
                              type="text"
                              placeholder="0"
                              value={empRetirementIncome}
                              onChange={handleCurrencyChange(setEmpRetirementIncome)}
                            />
                          </div>
                        </Form.Group>
                      </div>
                      <div className="pd6-field-row">
                        <Form.Group className="pd6-field" controlId="reviewEmpRetirementYears">
                          <Form.Label>Years</Form.Label>
                          <Form.Control
                            type="text"
                            value={empRetirementYears}
                            onChange={(e) => setEmpRetirementYears(e.target.value)}
                          />
                        </Form.Group>
                        <Form.Group className="pd6-field" controlId="reviewEmpRetirementMonths">
                          <Form.Label>Months</Form.Label>
                          <Form.Control
                            type="text"
                            value={empRetirementMonths}
                            onChange={(e) => setEmpRetirementMonths(e.target.value)}
                          />
                        </Form.Group>
                      </div>
                    </div>
                  )}
                  <Form.Group className="pd6-field" controlId="reviewEmpGrossIncome">
                    <Form.Label>
                      Gross monthly income<span className="pd6-required">*</span>
                    </Form.Label>
                    <div className="pd6-currency-input">
                      <span className="pd6-currency-input__prefix">$</span>
                      <Form.Control
                        type="number"
                        min={0}
                        value={empGrossMonthlyIncome}
                        onChange={(e) => setEmpGrossMonthlyIncome(e.target.value)}
                        isInvalid={
                          blockAttempted.ownershipEmployment &&
                          Boolean(blockErrors.ownershipEmployment.grossMonthlyIncome)
                        }
                      />
                    </div>
                    {blockAttempted.ownershipEmployment &&
                      blockErrors.ownershipEmployment.grossMonthlyIncome && (
                        <div className="pd6-field-error">
                          {blockErrors.ownershipEmployment.grossMonthlyIncome}
                        </div>
                      )}
                  </Form.Group>

                  <div className="pd6-field-row">
                    <Form.Group className="pd6-field" controlId="reviewEmpEmploymentYears">
                      <Form.Label>Years employed</Form.Label>
                      <Form.Control
                        type="text"
                        value={empEmploymentYears}
                        onChange={(e) => setEmpEmploymentYears(e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group className="pd6-field" controlId="reviewEmpEmploymentMonths">
                      <Form.Label>Months employed</Form.Label>
                      <Form.Control
                        type="text"
                        value={empEmploymentMonths}
                        onChange={(e) => setEmpEmploymentMonths(e.target.value)}
                      />
                    </Form.Group>
                  </div>

                  <Form.Group className="pd6-field" controlId="reviewEmpAdditionalIncome">
                    <Form.Label>Other income</Form.Label>
                    <div className="pd6-chip-group">
                      {ADDITIONAL_INCOME_SOURCE_OPTIONS.map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`pd6-chip ${
                            empAdditionalIncomeSource === option ? "pd6-chip--selected" : ""
                          }`}
                          onClick={() => setEmpAdditionalIncomeSource(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                    <div className="pd6-currency-input" style={{ marginTop: "8px" }}>
                      <span className="pd6-currency-input__prefix">$</span>
                      <Form.Control
                        type="text"
                        placeholder="0"
                        value={empAdditionalMonthlyIncome}
                        onChange={handleCurrencyChange(setEmpAdditionalMonthlyIncome)}
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="pd6-field" controlId="reviewCoApplicantToggle">
                    <div className="pd6-toggle-row">
                      <Form.Label className="pd6-toggle-row__label">Adding a co-applicant</Form.Label>
                      <Form.Check
                        type="switch"
                        id="reviewCoApplicantSwitch"
                        className="pd6-toggle"
                        checked={coAppIsCoApplicant}
                        onChange={(e) => setCoAppIsCoApplicant(e.target.checked)}
                        label=""
                      />
                    </div>
                  </Form.Group>

                  {coAppIsCoApplicant && (
                    <div className="pd6-reveal-panel">
                      <span className="pd6-reveal-panel__tag">Co-applicant</span>

                      <div className="pd6-field-row mt-3">
                        <Form.Group className="pd6-field" controlId="reviewCoAppFirstName">
                          <Form.Label>
                            First name<span className="pd6-required">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={coAppFirstName}
                            onChange={(e) => setCoAppFirstName(e.target.value)}
                            isInvalid={
                              blockAttempted.ownershipEmployment &&
                              Boolean(blockErrors.ownershipEmployment.coApplicantFirstName)
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {blockErrors.ownershipEmployment.coApplicantFirstName}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="pd6-field" controlId="reviewCoAppLastName">
                          <Form.Label>
                            Last name<span className="pd6-required">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={coAppLastName}
                            onChange={(e) => setCoAppLastName(e.target.value)}
                            isInvalid={
                              blockAttempted.ownershipEmployment &&
                              Boolean(blockErrors.ownershipEmployment.coApplicantLastName)
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {blockErrors.ownershipEmployment.coApplicantLastName}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </div>

                      <div className="pd6-field-row">
                        <Form.Group className="pd6-field" controlId="reviewCoAppEmail">
                          <Form.Label>
                            Email<span className="pd6-required">*</span>
                          </Form.Label>
                          <Form.Control
                            type="email"
                            value={coAppEmail}
                            onChange={(e) => setCoAppEmail(e.target.value)}
                            isInvalid={
                              blockAttempted.ownershipEmployment &&
                              Boolean(blockErrors.ownershipEmployment.coApplicantEmail)
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {blockErrors.ownershipEmployment.coApplicantEmail}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="pd6-field" controlId="reviewCoAppPhone">
                          <Form.Label>
                            Phone<span className="pd6-required">*</span>
                          </Form.Label>
                          <Form.Control
                            type="tel"
                            inputMode="numeric"
                            placeholder="(555)-555-5555"
                            value={coAppPhone}
                            onChange={(e) => {
                              const digits = e.target.value.replace(/[^0-9]/g, "");
                              if (digits.length <= 10) setCoAppPhone(formatPhoneNumber(digits));
                            }}
                            isInvalid={
                              blockAttempted.ownershipEmployment &&
                              Boolean(blockErrors.ownershipEmployment.coApplicantPhone)
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {blockErrors.ownershipEmployment.coApplicantPhone}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </div>

                      <div className="pd6-field-row">
                        <Form.Group className="pd6-field" controlId="reviewCoAppSsn">
                          <Form.Label>SSN</Form.Label>
                          <Form.Control
                            type="tel"
                            inputMode="numeric"
                            placeholder="123-45-6789"
                            value={coAppSsn}
                            onChange={(e) => {
                              const sanitized = e.target.value.replace(/\D/g, "");
                              if (sanitized.length < 10) setCoAppSsn(formatDashed(sanitized, [3, 5]));
                            }}
                          />
                        </Form.Group>
                        <Form.Group className="pd6-field" controlId="reviewCoAppCitizenship">
                          <Form.Label>Citizenship</Form.Label>
                          <div className="pd6-chip-group">
                            {CITIZENSHIP_OPTIONS.map((option) => (
                              <button
                                key={option}
                                type="button"
                                className={`pd6-chip ${
                                  coAppCitizenship === option ? "pd6-chip--selected" : ""
                                }`}
                                onClick={() => setCoAppCitizenship(option)}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        </Form.Group>
                      </div>

                      <Form.Group className="pd6-field" controlId="reviewCoAppDob">
                        <Form.Label>Date of birth</Form.Label>
                        <CoApplicantBirthday />
                        {blockAttempted.ownershipEmployment &&
                          blockErrors.ownershipEmployment.coApplicantDob && (
                            <div className="pd6-field-error">
                              {blockErrors.ownershipEmployment.coApplicantDob}
                            </div>
                          )}
                      </Form.Group>

                      <div className="pd6-field-row">
                        <Form.Group className="pd6-field" controlId="reviewCoAppSuffix">
                          <Form.Label>Suffix</Form.Label>
                          <div className="pd6-chip-group">
                            {SUFFIX_OPTIONS.map((option) => (
                              <button
                                key={option}
                                type="button"
                                className={`pd6-chip ${coAppSuffix === option ? "pd6-chip--selected" : ""}`}
                                onClick={() => setCoAppSuffix(option)}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        </Form.Group>
                        <Form.Group className="pd6-field" controlId="reviewCoAppMaritalStatus">
                          <Form.Label>Marital status</Form.Label>
                          <div className="pd6-chip-group">
                            {MARITAL_STATUS_OPTIONS.map((option) => (
                              <button
                                key={option}
                                type="button"
                                className={`pd6-chip ${
                                  coAppMaritalStatus === option ? "pd6-chip--selected" : ""
                                }`}
                                onClick={() => setCoAppMaritalStatus(option)}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        </Form.Group>
                      </div>

                      <Form.Group className="pd6-field" controlId="reviewCoAppResidentialAddress">
                        <CustomAddressAutocomplete
                          label="Residential address"
                          addressSearchQuery={coAppResidentialAddress}
                          setAddressSearchQuery={setCoAppResidentialAddress}
                          setAddressComponents={setCoAppResidentialAddressComponents}
                          addressValidationError={coAppResidentialAddressError}
                          setAddressValidationError={setCoAppResidentialAddressError}
                          placeholder="residential address"
                          localStorageLabel="coApplicantResidentialComponentsMazo"
                          isRequired={false}
                          onResolvingChange={handleAddressResolvingChange}
                        />
                        <Form.Control
                          type="text"
                          className="pd6-address-unit-input"
                          placeholder="Apt, suite, or unit (optional)"
                          autoComplete="off"
                          value={coAppResidentialUnitNumber}
                          onChange={(e) => setCoAppResidentialUnitNumber(e.target.value)}
                        />
                      </Form.Group>

                      <div className="pd6-field-row">
                        <Form.Group className="pd6-field" controlId="reviewCoAppLivingYears">
                          <Form.Label>Years at address</Form.Label>
                          <Form.Control
                            type="text"
                            value={coAppLivingYears}
                            onChange={(e) => setCoAppLivingYears(e.target.value)}
                          />
                        </Form.Group>
                        <Form.Group className="pd6-field" controlId="reviewCoAppLivingMonths">
                          <Form.Label>Months at address</Form.Label>
                          <Form.Control
                            type="text"
                            value={coAppLivingMonths}
                            onChange={(e) => setCoAppLivingMonths(e.target.value)}
                          />
                        </Form.Group>
                      </div>

                      <div className="pd6-field-row">
                        <Form.Group className="pd6-field" controlId="reviewCoAppMonthlyPayment">
                          <Form.Label>Monthly housing payment</Form.Label>
                          <div className="pd6-currency-input">
                            <span className="pd6-currency-input__prefix">$</span>
                            <Form.Control
                              type="text"
                              placeholder="0"
                              value={coAppLivingMonthlyPayment}
                              onChange={handleCurrencyChange(setCoAppLivingMonthlyPayment)}
                            />
                          </div>
                        </Form.Group>
                        <Form.Group className="pd6-field" controlId="reviewCoAppHousingStatus">
                          <Form.Label>Housing status</Form.Label>
                          <div className="pd6-chip-group pd6-chip-group--bands">
                            {HOUSING_STATUS_OPTIONS.map((option) => (
                              <button
                                key={option}
                                type="button"
                                className={`pd6-chip pd6-chip--band ${
                                  coAppLivingStatus === option ? "pd6-chip--selected" : ""
                                }`}
                                onClick={() => setCoAppLivingStatus(option)}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        </Form.Group>
                      </div>

                      <Form.Group className="pd6-field" controlId="reviewCoAppEmploymentStatus">
                        <Form.Label>
                          Employment status<span className="pd6-required">*</span>
                        </Form.Label>
                        <div className="pd6-chip-group">
                          {EMPLOYMENT_STATUS_OPTIONS.map((option) => (
                            <button
                              key={option}
                              type="button"
                              className={`pd6-chip ${
                                coAppEmploymentStatus === option ? "pd6-chip--selected" : ""
                              }`}
                              onClick={() => setCoAppEmploymentStatus(option)}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                        {blockAttempted.ownershipEmployment &&
                          blockErrors.ownershipEmployment.coApplicantEmploymentStatus && (
                            <div className="pd6-field-error">
                              {blockErrors.ownershipEmployment.coApplicantEmploymentStatus}
                            </div>
                          )}
                      </Form.Group>

                      {isWorking(coAppEmploymentStatus) && (
                        <div className="pd6-reveal-panel">
                          <span className="pd6-reveal-panel__tag">Co-applicant employer</span>
                          <div className="pd6-field-row mt-3">
                            <Form.Group className="pd6-field" controlId="reviewCoAppBusinessName">
                              <Form.Label>Business name</Form.Label>
                              <Form.Control
                                type="text"
                                value={coAppBusinessName}
                                onChange={(e) => setCoAppBusinessName(e.target.value)}
                              />
                            </Form.Group>
                            <Form.Group className="pd6-field" controlId="reviewCoAppGrossIncome">
                              <Form.Label>Gross monthly income</Form.Label>
                              <div className="pd6-currency-input">
                                <span className="pd6-currency-input__prefix">$</span>
                                <Form.Control
                                  type="text"
                                  placeholder="0"
                                  value={coAppGrossMonthlyIncome}
                                  onChange={handleCurrencyChange(setCoAppGrossMonthlyIncome)}
                                />
                              </div>
                            </Form.Group>
                          </div>
                          <Form.Group className="pd6-field" controlId="reviewCoAppEmployerAddress">
                            <CustomAddressAutocomplete
                              label="Employer address"
                              addressSearchQuery={coAppEmployerAddress}
                              setAddressSearchQuery={setCoAppEmployerAddress}
                              setAddressComponents={setCoAppEmployerAddressComponents}
                              addressValidationError={coAppEmployerAddressError}
                              setAddressValidationError={setCoAppEmployerAddressError}
                              placeholder="employer address"
                              localStorageLabel="coApplicantEmployerComponentsMazo"
                              isRequired={false}
                              onResolvingChange={handleAddressResolvingChange}
                            />
                            <Form.Control
                              type="text"
                              className="pd6-address-unit-input"
                              placeholder="Apt, suite, or unit (optional)"
                              autoComplete="off"
                              value={coAppEmployerUnitNumber}
                              onChange={(e) => setCoAppEmployerUnitNumber(e.target.value)}
                            />
                          </Form.Group>
                        </div>
                      )}

                      {isRetiredIsh(coAppEmploymentStatus) && (
                        <div className="pd6-reveal-panel">
                          <span className="pd6-reveal-panel__tag">Co-applicant retirement</span>
                          <div className="pd6-field-row mt-3">
                            <Form.Group className="pd6-field" controlId="reviewCoAppRetirementIncome">
                              <Form.Label>Retirement income</Form.Label>
                              <div className="pd6-currency-input">
                                <span className="pd6-currency-input__prefix">$</span>
                                <Form.Control
                                  type="text"
                                  placeholder="0"
                                  value={coAppRetirementIncome}
                                  onChange={handleCurrencyChange(setCoAppRetirementIncome)}
                                />
                              </div>
                            </Form.Group>
                          </div>
                          <div className="pd6-field-row">
                            <Form.Group className="pd6-field" controlId="reviewCoAppRetirementYears">
                              <Form.Label>Years</Form.Label>
                              <Form.Control
                                type="text"
                                value={coAppRetirementYears}
                                onChange={(e) => setCoAppRetirementYears(e.target.value)}
                              />
                            </Form.Group>
                            <Form.Group className="pd6-field" controlId="reviewCoAppRetirementMonths">
                              <Form.Label>Months</Form.Label>
                              <Form.Control
                                type="text"
                                value={coAppRetirementMonths}
                                onChange={(e) => setCoAppRetirementMonths(e.target.value)}
                              />
                            </Form.Group>
                          </div>
                        </div>
                      )}

                      <div className="pd6-field-row">
                        <Form.Group className="pd6-field" controlId="reviewCoAppEmploymentYears">
                          <Form.Label>Years employed</Form.Label>
                          <Form.Control
                            type="text"
                            value={coAppEmploymentYears}
                            onChange={(e) => setCoAppEmploymentYears(e.target.value)}
                          />
                        </Form.Group>
                        <Form.Group className="pd6-field" controlId="reviewCoAppEmploymentMonths">
                          <Form.Label>Months employed</Form.Label>
                          <Form.Control
                            type="text"
                            value={coAppEmploymentMonths}
                            onChange={(e) => setCoAppEmploymentMonths(e.target.value)}
                          />
                        </Form.Group>
                      </div>

                      <Form.Group className="pd6-field" controlId="reviewCoAppAdditionalIncome">
                        <Form.Label>Other income</Form.Label>
                        <div className="pd6-chip-group">
                          {ADDITIONAL_INCOME_SOURCE_OPTIONS.map((option) => (
                            <button
                              key={option}
                              type="button"
                              className={`pd6-chip ${
                                coAppAdditionalIncomeSource === option ? "pd6-chip--selected" : ""
                              }`}
                              onClick={() => setCoAppAdditionalIncomeSource(option)}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                        <div className="pd6-currency-input" style={{ marginTop: "8px" }}>
                          <span className="pd6-currency-input__prefix">$</span>
                          <Form.Control
                            type="text"
                            placeholder="0"
                            value={coAppAdditionalMonthlyIncome}
                            onChange={handleCurrencyChange(setCoAppAdditionalMonthlyIncome)}
                          />
                        </div>
                      </Form.Group>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <>
              {renderReadFields(ownershipEmploymentBlock)}
              {renderReadFields(ownershipEmploymentExtraBlock)}
              {additionalOwnerBlocks.map((block) => (
                <React.Fragment key={block.heading}>
                  {renderReadFields(block.fields, block.heading)}
                </React.Fragment>
              ))}
              {renderReadFields(coApplicantBlock, "Co-applicant")}
            </>
          )}
        </div>
      </div>

      <Form noValidate onSubmit={handleSubmit} className="pd6-form">
        <div className="pd6-review-bottom-row">
          <div className="pd6-review-block pd6-review-block--equipment">
            {renderCardHeader("Equipment", "equipment")}
            {editing.equipment ? (
              <div className="pd6-review-block__edit pd6-form">
                <Form.Group className="pd6-field" controlId="reviewEqYear">
                  <Form.Label>Year</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="YYYY"
                    autoComplete="off"
                    value={eqYear}
                    onChange={(e) => setEqYear(e.target.value)}
                    isInvalid={blockAttempted.equipment && Boolean(blockErrors.equipment.year)}
                  />
                  <Form.Control.Feedback type="invalid">{blockErrors.equipment.year}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="pd6-field" controlId="reviewEqMake">
                  <Form.Label>Make</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Make"
                    autoComplete="off"
                    value={eqMake}
                    onChange={(e) => setEqMake(e.target.value)}
                    isInvalid={blockAttempted.equipment && Boolean(blockErrors.equipment.make)}
                  />
                  <Form.Control.Feedback type="invalid">{blockErrors.equipment.make}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="pd6-field" controlId="reviewEqModel">
                  <Form.Label>Model</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Model"
                    autoComplete="off"
                    value={eqModel}
                    onChange={(e) => setEqModel(e.target.value)}
                    isInvalid={blockAttempted.equipment && Boolean(blockErrors.equipment.model)}
                  />
                  <Form.Control.Feedback type="invalid">{blockErrors.equipment.model}</Form.Control.Feedback>
                </Form.Group>
                {showReviewPurpleWaveIcn && (
                  <Form.Group className="pd6-field" controlId="reviewEqPurpleWaveIcn">
                    <Form.Label>Purple Wave item number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. EW4122"
                      autoComplete="off"
                      value={eqPurpleWaveIcn}
                      onChange={(e) => setEqPurpleWaveIcn(e.target.value)}
                    />
                  </Form.Group>
                )}
                <Form.Group className="pd6-field" controlId="reviewEqCost">
                  <Form.Label>
                    Equipment price<span className="pd6-required">*</span>
                  </Form.Label>
                  <div className="pd6-currency-input">
                    <span className="pd6-currency-input__prefix">$</span>
                    <Form.Control
                      type="text"
                      placeholder="0"
                      autoComplete="off"
                      value={eqCost}
                      onChange={handleEqCostChange}
                      isInvalid={blockAttempted.equipment && Boolean(blockErrors.equipment.cost)}
                    />
                  </div>
                  <div className="pd6-field-helper">
                    <span>Maximum Bid Amount</span>
                  </div>
                  {blockAttempted.equipment && blockErrors.equipment.cost && (
                    <div className="pd6-field-error">{blockErrors.equipment.cost}</div>
                  )}
                </Form.Group>
                <Form.Group className="pd6-field" controlId="reviewEqDeposit">
                  <Form.Label>Down Payment</Form.Label>
                  <div className="pd6-currency-input">
                    <span className="pd6-currency-input__prefix">$</span>
                    <Form.Control
                      type="text"
                      placeholder="0"
                      autoComplete="off"
                      value={eqDepositAmount}
                      onChange={handleEqDepositChange}
                      isInvalid={blockAttempted.equipment && Boolean(blockErrors.equipment.deposit)}
                    />
                  </div>
                  {blockAttempted.equipment && blockErrors.equipment.deposit && (
                    <div className="pd6-field-error">{blockErrors.equipment.deposit}</div>
                  )}
                </Form.Group>
                <Form.Group className="pd6-field" controlId="reviewEqPurchaseType">
                  <Form.Label>
                    Replacement or Addition<span className="pd6-required">*</span>
                  </Form.Label>
                  <div className="pd6-chip-group">
                    <button
                      type="button"
                      className={`pd6-chip ${eqPurchaseType === "Replacement" ? "pd6-chip--selected" : ""}`}
                      onClick={() => setEqPurchaseType("Replacement")}
                    >
                      Replacing existing equipment
                    </button>
                    <button
                      type="button"
                      className={`pd6-chip ${eqPurchaseType === "Expansion" ? "pd6-chip--selected" : ""}`}
                      onClick={() => setEqPurchaseType("Expansion")}
                    >
                      Adding to the fleet
                    </button>
                  </div>
                  {blockAttempted.equipment && blockErrors.equipment.purchaseType && (
                    <div className="pd6-field-error">{blockErrors.equipment.purchaseType}</div>
                  )}
                  {eqPurchaseType === "Replacement" && (
                    <div className="pd6-reveal-panel">
                      <span className="pd6-reveal-panel__tag">Replacement details</span>
                      <Form.Group className="pd6-field mt-2" controlId="reviewEqReplacingDescription">
                        <Form.Label>What is it replacing?</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. 2015 Bobcat S650 skid steer"
                          autoComplete="off"
                          value={eqReplacingDescription}
                          onChange={(e) => setEqReplacingDescription(e.target.value)}
                        />
                      </Form.Group>
                      <Form.Label>What will happen to it?</Form.Label>
                      <div className="pd6-chip-group">
                        {DISPOSITION_OPTIONS.map((option) => (
                          <button
                            key={option}
                            type="button"
                            className={`pd6-chip ${
                              eqReplacingDisposition === option ? "pd6-chip--selected" : ""
                            }`}
                            onClick={() => setEqReplacingDisposition(option)}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </Form.Group>
              </div>
            ) : (
              renderReadFields(equipmentBlock)
            )}
          </div>

          <div className="pd6-signature-card">
            <div className="pd6-signature-card__content">
            <div className="pd6-signature-card__eyebrow">Signature</div>
            <p className="pd6-signature-card__intro">
              Typing your name here is your legal signature. It covers both this application and
              permission for our lending partners to request your credit file.
            </p>

            <Form.Group className="pd6-field pd6-signature-card__field" controlId="stepReviewSignature">
              <Form.Label>
                Type your full legal name<span className="pd6-required">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                autoComplete="off"
                placeholder="Type your full name"
                minLength={3}
                maxLength={22}
                className="pd6-signature-card__input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  const key = e.key;
                  if (!/[a-zA-Z\s]/.test(key) && !["Backspace", "Tab", "ArrowLeft", "ArrowRight"].includes(key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Group>
            <canvas
              ref={canvasRef}
              width={300}
              height={100}
              style={{ display: "none" }}
            />

            <div className="pd6-checkbox-row pd6-signature-card__agree">
              <Form.Check type="checkbox" checked={agreed} onChange={() => setAgreed(!agreed)} />
              <span>
                I agree to{" "}
                <button
                  type="button"
                  className="pd6-link-button"
                  onClick={() => setModalIsOpen(true)}
                >
                  Credit Authorization
                </button>
                <span className="pd6-required">*</span>
              </span>
            </div>

            <div className="pd6-checkbox-row pd6-signature-card__agree">
              <Form.Check
                type="checkbox"
                checked={authorizeAgreed}
                onChange={() => setAuthorizeAgreed(!authorizeAgreed)}
              />
              <span>
                I authorize PAID and its lending partners to obtain credit reports on me
                {isBusiness ? " and on the business named above" : ""}, and confirm the
                information here is accurate.
                <span className="pd6-required">*</span>
              </span>
            </div>
            </div>
          </div>
        </div>

        <div className="pd6-step__nav">
          <div className="pd6-step__nav-buttons">
            <button
              type="button"
              className="pd6-btn pd6-btn--ghost"
              onClick={goBack}
              disabled={showLoader || isFirstStep}
            >
              <span className="pd6-btn__arrow">←</span>
              <span>Back</span>
            </button>
            <button type="submit" className="pd6-btn pd6-btn--submit" disabled={showLoader}>
              {showLoader ? (
                <Spinner style={{ width: "1.2rem", height: "1.2rem" }} />
              ) : (
                <>
                  <span>Sign &amp; submit</span>
                  <span className="pd6-btn__arrow">→</span>
                </>
              )}
            </button>
          </div>
          {footNote && <span className="pd6-step__foot-note">{footNote}</span>}
        </div>
      </Form>

      <DeclarationModal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        setAgreed={setAgreed}
        agreed={agreed}
        className="pd6-declaration-modal"
        overlayClassName="pd6-declaration-modal-overlay"
      />
    </div>
  );
};

export default StepReview;
